<template>
  <div>
    <b-modal
      :id="MODAL_ID"
      header-class="draggable-modal"
      title="Make Template Image Editable"
      size="lg"
      hide-footer
      no-close-on-backdrop
      @show="onModalOpen"
      @hide="onModalHide"
    >
      <div class="form-group">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <label
            class="mb-0"
            for="backgroundImageSelect"
          >Templates ({{ selectedTemplates.length }} selected)</label>
          <div>
            <a
              href="#"
              class="text-secondary small mr-2"
              @click.prevent="selectAllTemplates"
            >Select all</a>
            <a
              href="#"
              class="text-secondary small"
              @click.prevent="clearAllTemplates"
            >Clear</a>
          </div>
        </div>
        <Multiselect
          id="backgroundImageSelect"
          v-model="selectedTemplates"
          :options="backgroundImages"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          placeholder="Select templates"
          label="text"
          track-by="value"
          @input="onSelectedTemplatesChange"
        />
      </div>

      <div
        v-if="selectedTemplates.length > 0"
        class="template-navigation mb-3 p-2 border rounded d-flex justify-content-between align-items-center bg-light"
      >
        <div>
          <strong>Template {{ currentTemplateIndex + 1 }} of {{ selectedTemplates.length }}</strong> — {{ selectedTemplates[currentTemplateIndex]?.text }}
        </div>
        <div class="d-flex align-items-center gap-1">
          <button 
            v-for="(template, index) in selectedTemplates" 
            :key="template.value"
            class="btn btn-sm rounded-circle d-flex align-items-center justify-content-center p-0"
            :class="index === currentTemplateIndex ? 'btn-success text-white' : 'btn-outline-secondary'"
            style="width: 24px; height: 24px; font-size: 12px; margin-left: 4px;"
            @click="currentTemplateIndex = index"
          >
            <i
              v-if="hasConfigForTemplate(template.value) && index !== currentTemplateIndex"
              class="bi bi-check2"
            />
            <span v-else>{{ index + 1 }}</span>
          </button>
        </div>
      </div>

      <div>
        <b-card no-body>
          <b-tabs
            pills
            card
            fill
          >
            <b-tab active>
              <template #title>
                <i class="bi bi-fonts" /> Text Blocks
              </template>
              <div v-if="selectedBackgroundImageIndex !== 'none' && selectedImage">
                <div class="text-blocks-select-header">
                  <label class="mb-1">Select Text Blocks to Make Editable</label>
                  <button
                    v-b-tooltip.hover
                    type="button"
                    class="copy-text-blocks-header-btn"
                    title="Copy to clipboard"
                    aria-label="Copy to clipboard"
                    :disabled="isLoadingTextBlocks || !hasTextBlocksToCopy"
                    @click="copyTextBlocksToClipboard"
                  >
                    <i class="bi bi-clipboard" />
                  </button>
                </div>
                <Multiselect
                  v-model="selectedTextBlocks"
                  :options="isLoadingTextBlocks ? [] : detectedTextBlocks"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :placeholder="isLoadingTextBlocks ? 'Detecting text blocks...' : 'Select text blocks'"
                  :disabled="isLoadingTextBlocks"
                />
                <div
                  v-if="isLoadingTextBlocks"
                  class="text-center mt-3"
                >
                  <span
                    class="spinner-border spinner-border-sm"
                    role="status"
                  />
                  <span class="ml-2">Detecting text blocks...</span>
                </div>
              </div>
              <div
                v-else
                class="text-muted text-center py-3"
              >
                Select an image to detect text blocks.
              </div>
            </b-tab>
            <b-tab>
              <template #title>
                <i class="bi bi-cursor" /> Area Selection
              </template>
              <div v-if="selectedBackgroundImageIndex !== 'none' && selectedImage">
                <div class="form-group mb-2">
                  <span>Select the areas to make editable:</span>
                </div>
                <div class="text-removal-body">
                  <div class="d-flex flex-column gap-3">
                    <div 
                      class="image-container-wrapper"
                      @mousedown="startSelection"
                      @mousemove="updateSelection"
                      @mouseup="endSelection"
                      @mouseleave="endSelection"
                      @dragstart.prevent
                    >
                      <div
                        class="image-container"
                        :class="{ disabled: isLoading }"
                        @dragstart.prevent
                      >
                        <img
                          ref="zoomedImage"
                          :src="selectedImage"
                          class="zoomed-image"
                          draggable="false"
                        >
                      </div>

                      <!-- Canvas overlay for drawing selection borders -->
                      <canvas
                        ref="selectionCanvas"
                        class="selection-canvas"
                        style="pointer-events: none;"
                      />

                      <div
                        v-if="hasSelection || (isSelecting && imageSelection.width > 0 && imageSelection.height > 0)"
                        class="selection-preview"
                        :class="{ 'align-left': isMouseOnRightSide }"
                      >
                        <img
                          v-if="previewImage"
                          :src="previewImage"
                          class="preview-image"
                        >
                        <div class="preview-info">
                          <p>
                            Size: {{ Math.round(imageSelection.width) }}x{{ Math.round(imageSelection.height) }}px
                            <span 
                              v-if="shouldShowSizeWarning" 
                              v-b-tooltip.hover
                              class="size-warning"
                              title="Selecting an area with a width or height greater than 1024px may result in blurry output."
                            >
                              <i class="fas fa-exclamation-circle" />
                            </span>
                          </p>
                          <p>Position: ({{ Math.round(imageSelection.left) }}, {{ Math.round(imageSelection.top) }})</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </b-tab>
          </b-tabs>
        </b-card>
      </div>

      <div
        class="d-flex justify-content-between align-items-center p-2 cursor-pointer border rounded mt-2 mb-2"
        @click="isCollapsed = !isCollapsed"
      >
        <h6 class="mb-0">
          <span><i class="bi bi-sliders mr-1" />Options</span>
        </h6>
        <i :class="['fas', isCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']" />
      </div>
      <div v-show="!isCollapsed">
        <div class="row mb-3">
          <div class="col-md-6">
            <label>For infill, use:</label>
            <b-form-select
              v-model="infillMethod"
              :options="infillMethodOptions"
            />
          </div>
          <div class="col-md-6">
            <label>Template action:</label>
            <b-form-select
              v-model="templateAction"
              :options="templateActionOptions"
            />
          </div>
        </div>

        <div class="mb-3">
          <b-form-checkbox
            v-model="translateAfterExtract"
            :value="true"
            :unchecked-value="false"
          >
            Translate extracted text
          </b-form-checkbox>
          <div
            v-if="translateAfterExtract"
            class="mt-2"
          >
            <label>Target language(s):</label>
            <Multiselect
              v-model="translateTargetLanguages"
              :options="availableLanguages"
              :multiple="true"
              :close-on-select="true"
              :taggable="false"
              placeholder="Select language(s)..."
              class="multiselect-translation"
            />
          </div>
        </div>

        <div class="mb-3">
          <b-form-checkbox
            v-model="showAiEditorAfterMakeEditable"
            :value="true"
            :unchecked-value="false"
          >
            Show AI image editor after making text editable
          </b-form-checkbox>
        </div>
      </div>

      <div class="d-flex justify-content-between mt-2">
        <div class="d-flex gap-2">
          <button 
            type="button" 
            class="btn btn-outline-secondary mr-2" 
            :disabled="currentTemplateIndex === 0"
            @click="currentTemplateIndex--"
          >
            <i class="bi bi-chevron-left" /> Back
          </button>
          <button 
            type="button" 
            class="btn btn-outline-secondary" 
            :disabled="currentTemplateIndex >= Math.max(0, selectedTemplates.length - 1)"
            @click="currentTemplateIndex++"
          >
            Next <i class="bi bi-chevron-right" />
          </button>
        </div>

        <button
          type="button"
          class="btn btn-success"
          :disabled="selectedTemplates.length === 0 || (isLoading || isIdentifyingTextElements)"
          @click="handleRemove()"
        >
          <span
            v-if="(isLoading || isIdentifyingTextElements) && !expandInProgress"
            class="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
          />
          <i
            v-else
            class="bi bi-pencil"
          />
          {{ (isLoading || isIdentifyingTextElements) && !expandInProgress ? 'Processing...' : `Start (${selectedTemplates.length} templates)` }}
        </button>
      </div>

      <div
        v-if="selections.length > 0"
        class="selections-list mt-3"
      >
        <h6>Selected Areas ({{ selections.length }})</h6>
        <div class="d-flex flex-wrap">
          <div
            v-for="(selection, index) in selections"
            :key="`area-${index}`"
            class="selection-item mr-3 mb-2"
            :class="{ active: index === currentSelectionIndex }"
            @click="selectArea(index)"
          >
            <img
              :src="selection.previewImage"
              class="selection-thumbnail"
            >
            <button
              class="btn btn-sm btn-danger"
              @click.stop="removeSelection(index)"
            >
              ×
            </button>
          </div>
        </div>
        <button
          class="btn btn-sm btn-warning mt-2"
          @click="clearAllSelections"
        >
          Clear All
        </button>
      </div>
    </b-modal>
  </div>
</template>
  
  <script>
  /* eslint vue/enforce-style-attribute: 0 */
  import { mapGetters } from "vuex";
  import axios from "axios";
  import { makeTemplateImageEditableEventBroker, EVENTS as MODAL_EVENTS } from '@frontend/group/modules/make-template-image-editable/event-broker';
  import { FIELD_TYPE_BACKGROND_THEME_IMAGE } from "@frontend/constants/type-fields-of-template";
  import { addAddField, DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES } from '@frontend/group/modules/additional-fields';
  import { editorControlEventBroker, EVENTS } from "@frontend/group/modules/editor-control-popup/event-broker";
  import { HANDLER_TYPES as TRANSLATE_HANDLER_TYPES } from "@frontend/group/modules/translate-template";
  import { GLOBAL_UPDATE_LOCAL_STORAGE_KEY } from '@frontend/constants/global-update';
  import { deleteURLParameters, setURLParameters } from "@frontend/utils";
  import parseText from "@/common/preview/parse-text";
  import { canvasEditModeControl } from "@frontend/group/modules/canvas-edit-mode-control";
  import Requests from "@frontend/services/api/file-browser";
  import { uploadFolderFile, uploadFile } from "@frontend/services/file-uploader";
  import { createWorker } from 'tesseract.js';
  import Multiselect from 'vue-multiselect';
  import { ShowPageScopeCollector } from '@frontend/group/modules/scope-collectors/show-page-collector';
  import draggableModal from "@frontend/mixins/draggableModal";

  const getShowPageScope = () => ShowPageScopeCollector.get(['saveLayout', 'updateLayoutChanges', 'previews', 'handleObjectMoving', 'canvas_data', 'getChangesLayoutById', 'replaceBackgroundImage', 'reorderObjectCanvas', 'layoutChanges']);

  const MODAL_ID = 'make-template-image-editable-modal';
  const __measureCanvas = document.createElement('canvas');
  const __measureCtx = __measureCanvas.getContext('2d');

  export default {
      name: 'MakeTemplateImageEditableModal',
      components: {
          Multiselect,
      },
      mixins: [draggableModal],
      data() {
          return {
              MODAL_ID,
              isOpen: false,
              backgroundImages: [],
              selectedTemplates: [],
              currentTemplateIndex: 0,
              templateConfigurations: {},
              canvasSize: {
                  width: 0,
                  height: 0,
              },
              fallbackInstanceId: null,
              fallbackPreview: null,
              selectedImage: null,
              isZoomed: false,
              isSelecting: false,
              hasSelection: false,
              previewImage: null,
              editedImageUrl: null,
              selectionBox: {
                  left: 0,
                  top: 0,
                  width: 0,
                  height: 0
              },
              imageSelection: {
                  left: 0,
                  top: 0,
                  width: 0,
                  height: 0
              },
              startX: 0,
              startY: 0,
              imageStartX: 0,
              imageStartY: 0,
              isLoading: false,
              imageHistory: [], // Array to store previous image states
              containerBounds: null,
              imageBounds: null,
              displayedWidth: null,
              displayedHeight: null,
              offsetX: null,
              offsetY: null,
              scaleX: null,
              scaleY: null,
              expandInProgress: false,
              layoutId: 0,
              selections: [], // Array to store multiple selections
              currentSelectionIndex: -1, // Track which selection is being edited
              isMultiSelectMode: true, // Toggle for multi-select mode
              useLocalFontSizeEstimation: true,
              tessWorker: null,
              // New options for AI infill and template action
              infillMethod: 'gemini_nano_banana_pro',
              infillMethodOptions: [
                  { value: 'do_not_fill', text: 'Do not fill' },
                  { value: 'solid_color', text: 'Solid Color Fill' },
                  { value: 'gemini_nano_banana_pro', text: 'Nano Banana Pro' },
                  { value: 'gemini_nano_banana_2', text: 'Nano Banana 2' },
                  { value: 'gpt-image-2', text: 'GPT Image 2' }
              ],
              templateAction: 'update_current',
              templateActionOptions: [
                  { value: 'update_current', text: 'Update selected template' },
                  { value: 'backup_then_update', text: 'Backup original, then update' }
              ],
              textElementsInImage: [], // Cached AI-identified text elements
              lastIdentifiedImage: null, // Cached AI-identified image url
              isIdentifyingTextElements: false, // Loading state for text element identification
              // Translation options
              translateAfterExtract: false,
              translateTargetLanguages: [],
              // AI Editor option
              showAiEditorAfterMakeEditable: false,
              extractedTextsForAiPrompt: [], // Store extracted texts for AI prompt
              availableLanguages: [
                  'Arabic', 'Bengali', 'Cantonese', 'Czech', 'Dutch', 'English',
                  'French-CA', 'French-FR', 'German', 'Greek', 'Hebrew', 'Hindi',
                  'Italian', 'Japanese', 'Korean', 'Polish', 'Portuguese',
                  'Russian', 'Simplified Chinese', 'Slovak', 'Spanish (LatAm)', 'Spanish (Spain/EU)',
                  'Swedish', 'Thai', 'Traditional Chinese', 'Turkish', 'Vietnamese'
              ],
              isCollapsed: true,
              rootFolderName: 'Layouts',
              isMouseOnRightSide: false, // Track if mouse is on the right side to flip preview
              detectedTextBlocks: [],
              selectedTextBlocks: [],
              detectedTextBlocksMap: {},
              isLoadingTextBlocks: false,
              createdFieldIds: [],
          }
      },
      computed: {
          ...mapGetters("layout", ["layers", "target", "selectedInstance"]),
          currentTemplateOption() {
              if (this.selectedTemplates.length === 0) return null;
              return this.selectedTemplates[this.currentTemplateIndex];
          },
          instanceId() {
              return this.currentTemplateOption?.instanceId || this.fallbackInstanceId;
          },
          targetElement() {
              return this.currentTemplateOption?.target || null;
          },
          selectedBackgroundImageIndex: {
              get() {
                  if (this.selectedTemplates.length === 0) return 'none';
                  return this.selectedTemplates[this.currentTemplateIndex]?.value ?? 'none';
              },
              set(val) {
                  if (val === 'none') {
                      this.selectedTemplates = [];
                      this.currentTemplateIndex = 0;
                      this.onSelectedTemplatesChange();
                  } else {
                      const template = this.backgroundImages.find(opt => opt.value === val);
                      if (template) {
                          const existingIndex = this.selectedTemplates.findIndex(t => t.value === val);
                          if (existingIndex !== -1) {
                              this.currentTemplateIndex = existingIndex;
                          } else {
                              this.selectedTemplates = [template];
                              this.currentTemplateIndex = 0;
                              this.onSelectedTemplatesChange();
                          }
                      }
                  }
              }
          },
          isDefaultImage() {
              return this.selectedImage?.includes(DEFAULT_BACKGROUND_THEME_IMAGE_FIELD_PROPERTIES['additional_background_images']);
          },
          removeTextFromImage() {
              // Fill the selected area when infill method is NOT "do_not_fill"
              return this.infillMethod !== 'do_not_fill';
          },
          shouldShowSizeWarning() {
              // Calculate original dimensions based on scale
              const originalWidth = this.imageSelection.width * this.scaleX;
              const originalHeight = this.imageSelection.height * this.scaleY;
              return originalWidth > 1024 || originalHeight > 1024;
          },
          hasAnySelection() {
              return this.selections.length > 0 || this.hasSelection;
          },
          hasTextBlocksToCopy() {
              return (this.selectedTextBlocks?.length || this.detectedTextBlocks?.length || 0) > 0;
          },
      },
      watch: {
          currentTemplateIndex(newIdx, oldIdx) {
              if (oldIdx !== undefined && this.selectedTemplates[oldIdx]) {
                  const oldVal = this.selectedTemplates[oldIdx].value;
                  this.$set(this.templateConfigurations, oldVal, {
                      selectedTextBlocks: [...this.selectedTextBlocks],
                      selections: JSON.parse(JSON.stringify(this.selections)),
                      detectedTextBlocks: this.detectedTextBlocks,
                      detectedTextBlocksMap: this.detectedTextBlocksMap,
                      textElementsInImage: this.textElementsInImage,
                      lastIdentifiedImage: this.lastIdentifiedImage
                  });
              }
              if (newIdx !== undefined && this.selectedTemplates[newIdx]) {
                  const newVal = this.selectedTemplates[newIdx].value;
                  if (this.templateConfigurations[newVal]) {
                      this.selectedTextBlocks = this.templateConfigurations[newVal].selectedTextBlocks;
                      this.selections = this.templateConfigurations[newVal].selections;
                      this.detectedTextBlocks = this.templateConfigurations[newVal].detectedTextBlocks || [];
                      this.detectedTextBlocksMap = this.templateConfigurations[newVal].detectedTextBlocksMap || {};
                      this.textElementsInImage = this.templateConfigurations[newVal].textElementsInImage || [];
                      this.lastIdentifiedImage = this.templateConfigurations[newVal].lastIdentifiedImage || null;
                  } else {
                      this.selectedTextBlocks = [];
                      this.selections = [];
                      this.detectedTextBlocks = [];
                      this.detectedTextBlocksMap = {};
                      this.textElementsInImage = [];
                      this.lastIdentifiedImage = null;
                  }
              }
          },
          targetElement(newVal) {
              if (!newVal) {
                  return;
              }
              this.refreshImage();
          },
          selections: {
              deep: true,
              handler() { this.$nextTick(() => this.drawSelections()); }
          },
          currentSelectionIndex() {
              this.$nextTick(() => this.drawSelections());
          },
          isSelecting() {
              this.$nextTick(() => this.drawSelections());
          },
          selectionBox: {
              deep: true,
              handler() { this.$nextTick(() => this.drawSelections()); }
          },
          selectedImage(newVal) {
              if (!newVal) {
                  this.detectedTextBlocks = [];
                  this.selectedTextBlocks = [];
                  this.detectedTextBlocksMap = {};
              }
          },
      },
      mounted() {
          makeTemplateImageEditableEventBroker.on(MODAL_EVENTS.INIT, this.init.bind(this));
          makeTemplateImageEditableEventBroker.on(MODAL_EVENTS.CLOSED, this.onClose.bind(this));
      },
      methods: {
          selectAllTemplates() {
              this.selectedTemplates = [...this.backgroundImages];
              this.currentTemplateIndex = 0;
              this.onSelectedTemplatesChange();
          },
          clearAllTemplates() {
              this.selectedTemplates = [];
              this.currentTemplateIndex = 0;
              this.onSelectedTemplatesChange();
          },
          hasConfigForTemplate(value) {
              return this.templateConfigurations && this.templateConfigurations[value];
          },
          onSelectedTemplatesChange() {
              if (this.selectedTemplates.length > 0) {
                  this.fetchAllTextLinesForSelectedTemplates();
              } else {
                  this.detectedTextBlocks = [];
                  this.selectedTextBlocks = [];
                  this.detectedTextBlocksMap = {};
              }
          },
          async copyTextBlocksToClipboard() {
              const source = (this.selectedTextBlocks?.length ? this.selectedTextBlocks : this.detectedTextBlocks) || [];
              const texts = source
                  .map((t) => String(t ?? '').replace(/\s+/g, ' ').trim())
                  .filter(Boolean);

              if (texts.length === 0) {
                  toastr.error('No text blocks to copy.');
                  return;
              }
            
              const payload = texts.join('\n');

              try {
                  if (navigator?.clipboard?.writeText) {
                      await navigator.clipboard.writeText(payload);
                  } else {
                      const textarea = document.createElement('textarea');
                      textarea.value = payload;
                      textarea.setAttribute('readonly', '');
                      textarea.style.position = 'fixed';
                      textarea.style.opacity = '0';
                      document.body.appendChild(textarea);
                      textarea.select();
                      document.execCommand('copy');
                      document.body.removeChild(textarea);
                  }

                  toastr.success('Copied to clipboard.');
              } catch (e) {
                  console.error('Copy to clipboard failed:', e);
                  toastr.error('Failed to copy to clipboard.');
              }
          },
          async callOpenAI(settings) {
              const { data } = await axios.post(`/call-open-ai`, settings);
              return data;
          },
          async fetchAllTextLinesForSelectedTemplates() {
              if (!this.selectedTemplates || this.selectedTemplates.length === 0) {
                  return;
              }

              this.isLoadingTextBlocks = true;

              try {
                  for (let i = 0; i < this.selectedTemplates.length; i++) {
                      const template = this.selectedTemplates[i];
                      const templateValue = template.value;
                      
                      // Skip if we already detected texts for this template (even if 0 blocks)
                      if (this.templateConfigurations[templateValue] && this.templateConfigurations[templateValue].detectedTextBlocks) {
                          if (i === this.currentTemplateIndex) {
                              const config = this.templateConfigurations[templateValue];
                              this.detectedTextBlocks = config.detectedTextBlocks;
                              this.detectedTextBlocksMap = config.detectedTextBlocksMap;
                              this.selectedTextBlocks = config.selectedTextBlocks;
                          }
                          continue;
                      }
                      
                      const targetElement = template.target;
                      if (!targetElement) continue;

                      let originalSrc = targetElement?.getSrc();
                      if (!originalSrc || originalSrc.includes(';base64,')) {
                          continue;
                      }
                      originalSrc = deleteURLParameters(originalSrc, ['lightweight', 'full_resolution']);
                      originalSrc = setURLParameters(originalSrc, { full_resolution: true });
                      
                      let imageUrl = originalSrc;
                      if (imageUrl.startsWith("/")) {
                          imageUrl = window.location.origin + imageUrl;
                      }

                      const imageResponse = await fetch(imageUrl);
                      const blob = await imageResponse.blob();

                      const base64Image = await new Promise((resolve, reject) => {
                          const reader = new FileReader();
                          reader.onloadend = () => resolve(reader.result);
                          reader.onerror = reject;
                          reader.readAsDataURL(blob);
                      });

                      const prompt = "Perform a step-by-step visual analysis: 1. Identify all text regions overlaid on the background, noting their content, visual style, and precise rotation. 2. IMPORTANT: Do NOT include text that is printed ON product packaging (e.g., brand names on bottles, labels on boxes, etc.). 3. For each valid region, determine the tightest possible bounding box. 4. Group regions that share style using a common style_group ID. 5. Return JSON with blocks array."
                      const responseFormat = {
                        name: "extracted_blocks",
                        schema: {
                          type: "object",
                          properties: {
                            blocks: {
                              type: "array",
                              items: {
                                type: "object",
                                properties: {
                                  text: { type: "string" },
                                  left: { type: "number" },
                                  top: { type: "number" },
                                  width: { type: "number" },
                                  height: { type: "number" },
                                  style_group: { type: ["string", "number"] },
                                  color: { type: "string" },
                                  background_color: { type: "string" },
                                  font_weight: { type: "string", enum: ["normal", "bold"] },
                                  font_size: { type: "number" },
                                },
                                required: ["text", "left", "top", "width", "height", "style_group", "color", "background_color", "font_weight", "font_size"],
                                additionalProperties: false
                              }
                            }
                          },
                          required: ["blocks"],
                          additionalProperties: false
                        },
                        strict: true
                      };
                      
                      const formData = new FormData();
                      formData.append("responseFormat", JSON.stringify(responseFormat));
                      formData.append("prompt", prompt);
                      formData.append("file", base64Image);
                      const { data: responseData } = await axios.post('/ocr/recognize', formData);
                      
                      let contentString = null;
                      if (responseData && responseData.content) {
                          contentString = responseData.content;
                      } else if (responseData && responseData.choices && responseData.choices.length > 0) {
                          contentString = responseData.choices[0].message.content;
                      }

                      let detectedTextBlocks = [];
                      let detectedTextBlocksMap = {};

                      if (contentString) {
                          try {
                              const contentJson = JSON.parse(contentString);
                              if (contentJson && Array.isArray(contentJson.blocks)) {
                                  const filteredBlocks = contentJson.blocks.filter(b => b.text && b.text.length > 2);
                                  const uniqueTexts = [...new Set(filteredBlocks.map(b => b.text))];

                                  detectedTextBlocks = uniqueTexts;
                                  
                                  const map = {};
                                  filteredBlocks.forEach(b => {
                                      map[b.text] = {
                                          left:   Math.round(b.left),
                                          top:    Math.round(b.top),
                                          width:  Math.round(b.width),
                                          height: Math.round(b.height),
                                          style_group: String(b.style_group || 'default'),
                                          color: null,
                                          background_color: b.background_color || '#ffffff',
                                          font_weight: b.font_weight || 'normal',
                                          font_size: b.font_size || 50,
                                      };
                                  });
                                  detectedTextBlocksMap = map;
                              }
                          } catch (e) {
                              console.error("Failed to parse Text Blocks", e);
                          }
                      }

                      if (!this.templateConfigurations[templateValue]) {
                          this.$set(this.templateConfigurations, templateValue, {
                              selectedTextBlocks: [...detectedTextBlocks],
                              selections: [],
                              detectedTextBlocks: detectedTextBlocks,
                              detectedTextBlocksMap: detectedTextBlocksMap
                          });
                      } else {
                          const existingConfig = this.templateConfigurations[templateValue];
                          this.$set(this.templateConfigurations, templateValue, {
                              ...existingConfig,
                              detectedTextBlocks: detectedTextBlocks,
                              detectedTextBlocksMap: detectedTextBlocksMap,
                              selectedTextBlocks: [...detectedTextBlocks] 
                          });
                      }

                      if (i === this.currentTemplateIndex) {
                          const config = this.templateConfigurations[templateValue];
                          this.detectedTextBlocks = config.detectedTextBlocks;
                          this.detectedTextBlocksMap = config.detectedTextBlocksMap;
                          this.selectedTextBlocks = config.selectedTextBlocks;
                      }
                  }
              } catch (error) {
                  toastr.error("Failed to detect text blocks for some templates.");
              } finally {
                  this.isLoadingTextBlocks = false;
              }
          },

          openAiEditorIfEnabled() {
              if (!this.showAiEditorAfterMakeEditable || !this.selectedImage || typeof window.openImageGenrationModalWithImage !== 'function') {
                return;
              }

              const textsForPrompt = this.extractedTextsForAiPrompt.length > 0
                  ? this.extractedTextsForAiPrompt.map(t => `"${t}"`).join(', ')
                  : 'all text';

              const targetElementInfo = this.targetElement ? {
                  id: this.targetElement.id,
                  left: this.targetElement.left,
                  top: this.targetElement.top,
                  width: this.targetElement.width * (this.targetElement.scaleX || 1),
                  height: this.targetElement.height * (this.targetElement.scaleY || 1),
                  scaleX: this.targetElement.scaleX || 1,
                  scaleY: this.targetElement.scaleY || 1,
                  originalWidth: this.targetElement.width,
                  originalHeight: this.targetElement.height
              } : null;

              const prompt = `Remove the following text from this image: ${textsForPrompt}`;

              setTimeout(() => {
                  window.openImageGenrationModalWithImage(this.selectedImage, prompt, targetElementInfo);
              }, 300);
          },

          async processTextBlocks() {
              if (!this.selectedImage) return;

              // Look up cached bounding boxes for selected text blocks
              const blocks = (this.selectedTextBlocks || [])
                  .filter(text => this.detectedTextBlocksMap && this.detectedTextBlocksMap[text])
                  .map(text => ({ text, ...this.detectedTextBlocksMap[text] }));

              try {
                  const skipImageEdit = this.infillMethod !== 'solid_color';
                  const results = [];
                  const groupStyles = {};

                  if (blocks.length > 0) {
                      // Group blocks by style_group
                      const groups = {};
                      blocks.forEach(block => {
                          const gid = block.style_group || 'default';
                          if (!groups[gid]) groups[gid] = [];
                          groups[gid].push(block);
                      });

                      // Process groups: first element of each group detects style, rest reuse it
                      for (const gid in groups) {
                          const groupBlocks = groups[gid];
                          const firstBlock = groupBlocks[0];
                          
                          const firstSelection = {
                              left: firstBlock.left,
                              top: firstBlock.top,
                              width: firstBlock.width,
                              height: firstBlock.height
                          };

                          const firstHintStyle = {
                              color: firstBlock.color,
                              background_color: firstBlock.background_color,
                              font_weight: firstBlock.font_weight,
                              font_size: firstBlock.font_size,
                          };

                          const firstResult = await this.processSelection(firstSelection, null, true, skipImageEdit, true, firstBlock.text, firstHintStyle);
                          results.push(firstResult);
                          if (firstResult?.createdField?.fieldName) {
                              this.createdFieldIds.push(firstResult.createdField.fieldName);
                          }

                          if (firstResult && firstResult.style) {
                              groupStyles[gid] = firstResult.style;
                          }

                          if (groupBlocks.length > 1) {
                              for (const block of groupBlocks.slice(1)) {
                                  const imageSelection = {
                                      left: block.left,
                                      top: block.top,
                                      width: block.width,
                                      height: block.height
                                  };
                                  const result = await this.processSelection(imageSelection, null, true, skipImageEdit, true, block.text, groupStyles[gid]);
                                  results.push(result);
                                  if (result?.createdField?.fieldName) {
                                      this.createdFieldIds.push(result.createdField.fieldName);
                                  }
                              }
                          }
                      }
                  }

                  if (['gemini_nano_banana_pro', 'gemini_nano_banana_2', 'gpt-image-2'].includes(this.infillMethod)) {
                      let imageUrl = this.selectedImage;

                      if (imageUrl.startsWith("/")) {
                          imageUrl = window.location.origin + imageUrl;
                      }

                      const response = await fetch(imageUrl);
                      const blob = await response.blob();

                      const file = new File([blob], 'image.png', { type: blob.type });

                      const formData = new FormData();

                      formData.append('image', file);
                      formData.append('infillMethod', this.infillMethod);
                      formData.append('elementsToRemove', JSON.stringify(this.detectedTextBlocksMap));

                      const img = new Image();

                      await new Promise((resolve, reject) => { img.onload = resolve; img.onerror = reject; img.src = imageUrl; });

                      formData.append('width', img.width);
                      formData.append('height', img.height);

                      const result = await axios.post('/ai-edit-image', formData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                      });

                      if (result.data && result.data.url) {
                          this.selectedImage = result.data.url;
                          this.editedImageUrl = result.data.url;
                      }

                      await this.handleSave();
                  } else if (this.infillMethod === 'solid_color') {
                      const edits = results.filter(Boolean);

                      if (edits.length > 0) {
                          await this.applyImageEditsSequentially(edits);
                      }

                      await this.handleSave();
                  } else {
                      if (this.selectedImage) {
                          await this.saveImageToLayoutsFolder(this.selectedImage);
                      }
                  }

                  this.openAiEditorIfEnabled();
              } catch (error) {
                  console.log('Failed to process text blocks.');
                  console.error(error);
                  toastr.error("Failed to process text blocks.");
              } finally {
                  this.extractedTextsForAiPrompt = [];
              }
          },

          init({ allBackgroundImages, clickedInstanceId, preview }) {
              this.fallbackInstanceId = clickedInstanceId;
              this.fallbackPreview = preview;

              // Use preview's canvas size or the first one available
              this.canvasSize = {
                  width: preview?.canvas?.width || 0,
                  height: preview?.canvas?.height || 0,
              };

              this.backgroundImages = [
                  {
                      text: "Select Image",
                      value: "none"
                  },
                  ...allBackgroundImages.map((img, index) => ({
                      ...img,
                      text: img.name,
                      value: index // use simple index or string
                  }))
              ];
              this.selectedBackgroundImageIndex = "none";

              const url = window.location.href;
              const match = url.match(/\/group\/(\d+)\//);

              if (match) {
                  this.layoutId = parseInt(match[1], 10);
              }

              // Select the image that corresponds to the active object in the clicked preview
              const activeObjectId = preview?.canvas?.getActiveObject()?.id;
              if (activeObjectId) {
                  const matchingImage = this.backgroundImages.find(img => img.instanceId === clickedInstanceId && img.id === activeObjectId);
                  if (matchingImage) {
                      this.selectedBackgroundImageIndex = matchingImage.value;
                  }
              }

              // If there's exactly one image option (plus the placeholder), pre-select it
              if (this.selectedBackgroundImageIndex === 'none' && this.backgroundImages.length === 2) {
                this.selectedBackgroundImageIndex = this.backgroundImages[1].value;
              }

              this.$bvModal.show(MODAL_ID);

              this.$nextTick(() => {
                this.initDraggableModal(MODAL_ID);
              });
          },
          onClose() {
              this.$nextTick(() => {
                  this.$bvModal.hide(MODAL_ID);
              });
          },
          async onModalOpen() {
              // Reset infill method to default (Nano Banana Pro) when modal opens
              this.infillMethod = 'gemini_nano_banana_pro';
              this.createdFieldIds = [];
          },
          async onModalHide() {
            this.selectedBackgroundImageIndex = 'none';
            this.createdFieldIds = [];
              // ...
          },
          refreshImage() {
              if (!this.targetElement) {
                  this.selectedImage = null;
                  this.imageHistory = [];
                  this.isZoomed = false;
                  this.resetSelection();
                  return;
              }
  
              // Check if the selected element is an image
              const isImage = this.targetElement.type === 'image' || 
                  (this.targetElement.id && 
                   (this.targetElement.id.includes('image_') || 
                    this.targetElement.id.includes('product_image')));
  
              if (isImage) {
                  //ignore base64 images like tables
                  if (this.targetElement?.getSrc().includes(';base64,')) {
                    this.selectedImage = null;
                    this.imageHistory = [];
                    this.isZoomed = false;
                    this.resetSelection();
                    return;
                  }
  
                  // Get the original image source
                  let originalSrc = deleteURLParameters(this.targetElement?.getSrc(), ['lightweight', 'full_resolution']);
                  originalSrc = setURLParameters(originalSrc, { full_resolution: true });
  
                  // Only reset if the image has actually changed
                  if (this.selectedImage !== originalSrc) {
                      this.selectedImage = originalSrc;
                      this.imageHistory = [];
                      this.isZoomed = false;
                      this.resetSelection();
                      // Identify text elements in the image for AI infill
                      this.identifyTextElements();
                  }
                  
                  // If it's the default image, treat it as if it's not an image
                  if (this.isDefaultImage) {
                      this.selectedImage = null;
                      this.imageHistory = [];
                      this.isZoomed = false;
                      this.resetSelection();
                  }
              } else {
                  this.selectedImage = null;
                  this.imageHistory = [];
                  this.isZoomed = false;
                  this.resetSelection();
              }
          },
          async identifyTextElements() {
              // Only identify if we're using AI infill method
              if (this.infillMethod !== 'gemini_nano_banana_pro') {
                  this.textElementsInImage = [];
                  return;
              }

              if (!this.selectedImage) {
                  this.textElementsInImage = [];
                  return;
              }

              // Use cached data if the image hasn't changed
              if (this.lastIdentifiedImage === this.selectedImage && this.textElementsInImage.length > 0) {
                  return;
              }
              // Also check template config just in case it was cached but not loaded into state
              if (this.currentTemplateOption && this.templateConfigurations[this.currentTemplateOption.value]) {
                  const config = this.templateConfigurations[this.currentTemplateOption.value];
                  if (config.lastIdentifiedImage === this.selectedImage && config.textElementsInImage) {
                      this.textElementsInImage = config.textElementsInImage;
                      this.lastIdentifiedImage = config.lastIdentifiedImage;
                      return;
                  }
              }

              this.isIdentifyingTextElements = true;
              try {
                  // Fetch the image and convert to blob
                  const response = await fetch(this.selectedImage);
                  const blob = await response.blob();
                  const file = new File([blob], 'image.png', { type: blob.type });

                  const formData = new FormData();
                  formData.append('image', file);

                  const result = await axios.post('/ai-identify-text-elements', formData, {
                      headers: { 'Content-Type': 'multipart/form-data' }
                  });

                  this.textElementsInImage = result.data.elements || [];
                  this.lastIdentifiedImage = this.selectedImage;
                  
                  if (this.currentTemplateOption && this.templateConfigurations[this.currentTemplateOption.value]) {
                      this.templateConfigurations[this.currentTemplateOption.value].textElementsInImage = this.textElementsInImage;
                      this.templateConfigurations[this.currentTemplateOption.value].lastIdentifiedImage = this.lastIdentifiedImage;
                  }
                  
                  console.log('Identified text elements:', this.textElementsInImage);
              } catch (error) {
                  console.error('Error identifying text elements:', error);
                  this.textElementsInImage = [];
              } finally {
                  this.isIdentifyingTextElements = false;
              }
          },
          startSelection(event) {
              if (this.isLoading) return;
              
              const imageRect = this.$refs.zoomedImage.getBoundingClientRect();
              const wrapperRect = event.currentTarget.getBoundingClientRect();
              
              // Store container bounds for later use
              this.containerBounds = wrapperRect;
              this.imageBounds = imageRect;
              
              // Calculate the actual displayed image dimensions based on aspect ratio
              const displayedWidth = imageRect.width;
              const displayedHeight = imageRect.height;
  
              this.displayedWidth = displayedWidth;
              this.displayedHeight = displayedHeight;
  
              // Calculate the scaling factor between displayed image and natural image dimensions
              this.scaleX = this.$refs.zoomedImage.naturalWidth / displayedWidth;
              this.scaleY = this.$refs.zoomedImage.naturalHeight / displayedHeight;
              
              // Store image offset from wrapper
              this.offsetX = imageRect.left - wrapperRect.left;
              this.offsetY = imageRect.top - wrapperRect.top;
              
              // Calculate position relative to the wrapper
              const relativeX = event.clientX - wrapperRect.left;
              const relativeY = event.clientY - wrapperRect.top;
              
              // Calculate position relative to the image
              const imageX = relativeX - this.offsetX;
              const imageY = relativeY - this.offsetY;
              
              // Store the container coordinates for visual representation
              this.startX = relativeX;
              this.startY = relativeY;
              
              // Store image coordinates for the actual selection
              this.imageStartX = imageX;
              this.imageStartY = imageY;
              
              this.imageSelection = {
                  left: Math.round(Math.max(0, imageX) * this.scaleX),
                  top: Math.round(Math.max(0, imageY) * this.scaleY),
                  width: 0,
                  height: 0
              };
              
              this.isSelecting = true;
              this.hasSelection = false;
              
              this.selectionBox = {
                  left: this.startX,
                  top: this.startY,
                  width: 0,
                  height: 0
              };
  
              // Add global mouse event listeners to continue tracking outside container
              document.addEventListener('mousemove', this.handleGlobalMouseMove);
              document.addEventListener('mouseup', this.handleGlobalMouseUp);
          },
          handleGlobalMouseMove(event) {
              if (!this.isSelecting || this.isLoading) return;
              
              // Get the current mouse position relative to wrapper for visual representation
              const currentX = event.clientX - this.containerBounds.left;
              const currentY = event.clientY - this.containerBounds.top;
              
              // Track if mouse is on the right half of the container
              this.isMouseOnRightSide = currentX > (this.containerBounds.width / 2);
              
              // Update the selection box visually (this can extend outside the image)
              this.selectionBox = {
                  left: Math.min(this.startX, currentX),
                  top: Math.min(this.startY, currentY),
                  width: Math.abs(currentX - this.startX),
                  height: Math.abs(currentY - this.startY)
              };
  
              // Calculate image coordinates for actual selection
              const imageLeft = Math.max(0, Math.min(this.imageStartX, this.imageStartX + (currentX - this.startX)));
              const imageTop = Math.max(0, Math.min(this.imageStartY, this.imageStartY + (currentY - this.startY)));
              const imageRight = Math.min(this.displayedWidth, Math.max(this.imageStartX, this.imageStartX + (currentX - this.startX)));
              const imageBottom = Math.min(this.displayedHeight, Math.max(this.imageStartY, this.imageStartY + (currentY - this.startY)));
              
              // Update image coordinates for actual selection
              this.imageSelection = {
                  left: Math.round(imageLeft * this.scaleX),
                  top: Math.round(imageTop * this.scaleY),
                  width: Math.round((imageRight - imageLeft) * this.scaleX),
                  height: Math.round((imageBottom - imageTop) * this.scaleY)
              };
              
              // If selection is valid (has width and height), set hasSelection to true
              this.hasSelection = this.imageSelection.width > 0 && this.imageSelection.height > 0;
              
              // Generate preview of selected region in real-time during selection
              if (this.hasSelection) {
                  this.generatePreview();
              }
          },
          updateSelection(event) {
              if (!this.isSelecting || this.isLoading) return;
              // This is still needed for direct container events
              this.handleGlobalMouseMove(event);
          },
          handleGlobalMouseUp() {
              this.endSelection();
          },
          endSelection() {
              if (!this.isSelecting || this.isLoading) return;
              
              this.isSelecting = false;
              
              // Remove global event listeners
              document.removeEventListener('mousemove', this.handleGlobalMouseMove);
              document.removeEventListener('mouseup', this.handleGlobalMouseUp);
              
              // If we have a valid selection, add it to selections array
              if (this.hasSelection) {
                  const adjustedDisplayBox = {
                      left: this.selectionBox.left - (this.offsetX || 0),
                      top: this.selectionBox.top - (this.offsetY || 0),
                      width: this.selectionBox.width,
                      height: this.selectionBox.height,
                  };
                  
                  const newSelection = {
                      imageSelection: { ...this.imageSelection },
                      displayBox: adjustedDisplayBox,
                      previewImage: this.previewImage
                  };
                  
                  if (this.isMultiSelectMode) {
                      this.selections.push(newSelection);
                      this.currentSelectionIndex = this.selections.length - 1;
                      // Don't reset selection in multi-mode, keep the preview visible
                      // this.resetSelection();
                  } else {
                      // Single selection mode - replace existing
                      this.selections = [newSelection];
                      this.currentSelectionIndex = 0;
                  }
              }
          },
          generatePreview() {
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              const img = new Image();
  
              img.onload = () => {
                  // Ensure selection dimensions are valid
                  if (this.imageSelection.width <= 0 || this.imageSelection.height <= 0) {
                      this.previewImage = null;
                      return;
                  }
                  
                  // Use the actual image dimensions for accurate cropping
                  canvas.width = this.imageSelection.width;
                  canvas.height = this.imageSelection.height;
                  
                  // Clear the canvas with transparency
                  ctx.clearRect(0, 0, canvas.width, canvas.height);
  
                  try {
                      ctx.drawImage(
                          img,
                          this.imageSelection.left,
                          this.imageSelection.top,
                          this.imageSelection.width,
                          this.imageSelection.height,
                          0,
                          0,
                          this.imageSelection.width,
                          this.imageSelection.height
                      );
  
                      this.previewImage = canvas.toDataURL('image/png');
                  } catch(e) {
                      console.error('Error generating preview:', e);
                      this.previewImage = null;
                  }
              };
  
              img.src = this.selectedImage;
          },
          resetSelection() {
              this.isSelecting = false;
              this.hasSelection = false;
              this.previewImage = null;
              this.selectionBox = {
                  left: 0,
                  top: 0,
                  width: 0,
                  height: 0
              };
              this.imageSelection = {
                  left: 0,
                  top: 0,
                  width: 0,
                  height: 0
              };
          },
          async handleRemove() {
              if (this.isLoading) return;
              this.isLoading = true;
              this.createdFieldIds = [];

              const originalIndex = this.currentTemplateIndex;
              const originalHasSelection = this.hasSelection;
              const originalImageSelection = this.imageSelection;
              const originalPreviewImage = this.previewImage;

              const shouldBackupFirst = this.templateAction === 'backup_then_update' && this.removeTextFromImage;
              let anyBackupCreated = false;

              try {
                  for (let i = 0; i < this.selectedTemplates.length; i++) {
                      // Switch to the template
                      this.currentTemplateIndex = i;
                      await this.$nextTick();
                      // Short delay to ensure DOM and computed properties have settled
                      await new Promise(resolve => setTimeout(resolve, 50));

                      const isCurrentOriginal = (i === originalIndex);
                      const currentHasSelection = isCurrentOriginal ? originalHasSelection : false;
                      const currentImageSelection = isCurrentOriginal ? originalImageSelection : null;
                      const currentPreviewImage = isCurrentOriginal ? originalPreviewImage : null;

                      // Capture target element coordinates
                      if (this.targetElement && this.targetElement.aCoords) {
                          this._capturedTargetCoords = {
                              tl: { x: this.targetElement.aCoords.tl.x, y: this.targetElement.aCoords.tl.y },
                              br: { x: this.targetElement.aCoords.br.x, y: this.targetElement.aCoords.br.y }
                          };
                      }

                      if (shouldBackupFirst) {
                          const backupCreated = await this.createBackupCopy();
                          if (!backupCreated) {
                              continue;
                          }
                          anyBackupCreated = true;
                      }

                      if (this.selections.length === 0 && !currentHasSelection) {
                          await this.processTextBlocks();
                      } else if (this.isMultiSelectMode && this.selections.length > 0) {
                          await this.processMultipleSelections();
                      } else if (currentHasSelection && this.selectedImage) {
                          if (this.infillMethod === 'gemini_nano_banana_pro' && this.removeTextFromImage) {
                               const result = await this.processSelection(currentImageSelection, currentPreviewImage, false, true);
                               if (result?.createdField?.fieldName) {
                                   this.createdFieldIds.push(result.createdField.fieldName);
                               }
                               if (result && result.text) {
                                   await this.performBatchAiEdit([result.text]);
                               }

                               await this.handleSave(); 
                          } else {
                              const result = await this.processSelection(currentImageSelection, currentPreviewImage, false, false);
                              if (result?.createdField?.fieldName) {
                                  this.createdFieldIds.push(result.createdField.fieldName);
                              }
                              if (this.removeTextFromImage) {
                                  await this.handleSave();
                              } else {
                                  await this.saveImageToLayoutsFolder(this.selectedImage);
                              }
                          }

                          this.resetSelection();
                      }

                      const createdFieldIdsSnapshot = [...new Set(this.createdFieldIds.filter(Boolean))];
                      if (this.translateAfterExtract && this.translateTargetLanguages.length > 0 && createdFieldIdsSnapshot.length > 0) {
                          await this.triggerTranslation(createdFieldIdsSnapshot);
                      }
                      
                      this.openAiEditorIfEnabled();
                      this.createdFieldIds = [];
                  }

                  this.$bvModal.hide(MODAL_ID);

                  if (anyBackupCreated) {
                      await this.saveLayoutAndReload();
                  }

                  this.currentTemplateIndex = originalIndex;

              } finally {
                  this.isLoading = false;
                  this._capturedTargetCoords = null;
                  this.extractedTextsForAiPrompt = [];
              }
          },

          async saveLayoutAndReload() {
              try {
                  toastr.info('Saving changes and reloading...');

                  // Trigger the save button click to save layout changes
                  const $btnSave = $('.btn-save-changes');
                  if ($btnSave.length && !$btnSave.prop('disabled')) {
                      // Wait for save to complete by listening to loading overlay
                      await new Promise((resolve) => {
                          $btnSave.click();
                          // Wait a moment for the save to initiate, then wait for overlay to hide
                          setTimeout(() => {
                              const checkSaveComplete = setInterval(() => {
                                  if (!$('.loadingOverlay').is(':visible')) {
                                      clearInterval(checkSaveComplete);
                                      resolve();
                                  }
                              }, 500);
                              // Timeout after 30 seconds
                              setTimeout(() => {
                                  clearInterval(checkSaveComplete);
                                  resolve();
                              }, 30000);
                          }, 1000);
                      });
                  }

                  // Reload the page to show the backup template
                  window.location.reload();
              } catch (error) {
                  console.error('Error saving layout:', error);
                  // Reload anyway to show backup
                  window.location.reload();
              }
          },

          async createBackupCopy() {
              try {
                  toastr.info('Creating backup copy of original template...');

                  // Get required IDs
                  const grid = $("#template-group-preview");
                  const layoutId = grid.attr("data-layout-id");
                  const preview = this.currentTemplateOption?.preview || this.fallbackPreview;
                  const templateId = preview?.template?.id || grid.attr("data-template-id");
                  let customerId = preview?.template?.customer_id || grid.attr("data-customer-id");

                  // Handle "all customers" case
                  const isAllCustomer = parseInt(customerId) === 0;
                  if (isAllCustomer && preview?.template?.customer_id) {
                      customerId = preview.template.customer_id;
                  }

                  const url = `/banner/${customerId}/group/${layoutId}/template/${this.instanceId}/${templateId}/duplicate?old-customer=0`;
                  const response = await axios.post(url, {
                      forceReturnInstanceId: true
                  });

                  if (response.data && response.data.instance_id) {
                      toastr.success('Backup copy created! Now processing current template...');
                      return true;
                  } else {
                      throw new Error('Failed to create backup');
                  }
              } catch (error) {
                  console.error('Error creating backup copy:', error);
                  toastr.error('Failed to create backup copy. Please try again.');
                  return false;
              }
          },

          async processMultipleSelections() {
            this.isLoading = true;
            try {
                // Check if using Nano Banana Pro for batch processing
                if (this.infillMethod === 'gemini_nano_banana_pro' && this.removeTextFromImage) {
                    // 1. Process all selections to extract text and add to canvas (skip individual AI edits)
                    const extractionTasks = this.selections.map((selection) =>
                        this.processSelection(selection.imageSelection, selection.previewImage, true, true)
                    );
                    
                    const extractionResults = await Promise.all(extractionTasks);
                    console.log('Batch Edit (Nano Banana Pro) - Extraction Results:', extractionResults);

                    extractionResults.forEach(result => {
                        if (result?.createdField?.fieldName) {
                            this.createdFieldIds.push(result.createdField.fieldName);
                        }
                    });

                    const extractedTexts = extractionResults
                        .filter(r => r && r.text)
                        .map(r => r.text); // Collect all extracted text
                    
                    console.log('Batch Edit (Nano Banana Pro) - Extracted Texts:', extractedTexts);

                    // 2. Perform a single AI edit on the full image
                    if (extractedTexts.length > 0) {
                        await this.performBatchAiEdit(extractedTexts);
                    } else {
                        console.warn('Batch Edit (Nano Banana Pro) - No text extracted to remove. Skipping AI edit and using original image.');
                        // Fallback: if no text found to remove, keep original image (background) 
                        // but allow saving the added text elements (added in step 1).
                        this.editedImageUrl = this.selectedImage;
                    }

                    this.clearAllSelections();
                    await this.handleSave(); // Save the final edited image
                } else {
                    // Original flow: Process each selection individually (OCR + Edit)
                    const tasks = this.selections.map((selection) =>
                        this.processSelection(selection.imageSelection, selection.previewImage, true, false)
                    );

                    const editOps = await Promise.all(tasks); // array of edit descriptors or nulls

                    editOps.forEach(result => {
                        if (result?.createdField?.fieldName) {
                            this.createdFieldIds.push(result.createdField.fieldName);
                        }
                    });

                    this.clearAllSelections();

                    if (this.removeTextFromImage) {
                        const edits = editOps.filter(Boolean);
                        if (edits.length > 0) {
                            await this.applyImageEditsSequentially(edits);
                        }
                        await this.handleSave();
                    } else {
                        if (this.selectedImage) {
                            await this.saveImageToLayoutsFolder(this.selectedImage);
                        }
                    }
                }

                this.openAiEditorIfEnabled();
            } catch (error) {
                console.error('Error processing multiple selections:', error);
                alert('Failed to process selections. Please try again.');
            } finally {
                this.isLoading = false;
                // Reset extracted texts for next use
                this.extractedTextsForAiPrompt = [];
            }
          },

          async performBatchAiEdit(elementsToRemove) {
              if (!this.selectedImage) return;

              try {
                  // Fetch the image and convert to blob
                  const response = await fetch(this.selectedImage);
                  const blob = await response.blob();
                  const file = new File([blob], 'image.png', { type: blob.type });

                  const formData = new FormData();
                  formData.append('image', file);
                  formData.append('infillMethod', 'gemini_nano_banana_pro');
                  formData.append('width', this.$refs.zoomedImage.naturalWidth);
                  formData.append('height', this.$refs.zoomedImage.naturalHeight);
                  formData.append('elementsToRemove', JSON.stringify(elementsToRemove));

                  const result = await axios.post('/ai-edit-image', formData, {
                      headers: { 'Content-Type': 'multipart/form-data' }
                  });

                  if (result.data && result.data.url) {
                       this.selectedImage = result.data.url;
                       this.editedImageUrl = result.data.url;
                  }
              } catch (error) {
                  console.error('Error performing batch AI edit:', error);
                  
                  let errorMessage = 'Failed to perform batch AI edit.';
        
                  if (error.response) {
                      // Use server-provided error message if available
                      if (error.response.data && error.response.data.error) {
                          errorMessage = error.response.data.error;
                      } else if (error.response.status === 503) {
                          errorMessage = "The AI model is currently experiencing high demand. Please try again later.";
                      }
                  }
                  
                  toastr.error(errorMessage);
              }
          },

          computeFittingFontSize({
            text,
            fontFamily = 'Arial, sans-serif',
            boxW,
            boxH,
            min = 8,
            max = 200,
            paddingRatio = 0.05,     // 5% internal padding per side
            lineHeight = 1.2,        // line height multiplier
            clamp = 72               // optional upper cap
          }) {
            const padX = boxW * paddingRatio;
            const padY = boxH * paddingRatio;
            const innerW = Math.max(1, boxW - 2 * padX);
            const innerH = Math.max(1, boxH - 2 * padY);

            // Normalize line breaks
            const paragraphs = String(text ?? '').replace(/\r/g, '').split('\n').map(s => s.trimEnd());

            function setFont(px) {
              __measureCtx.font = `${px}px ${fontFamily}`;
            }

            function wrapParagraph(px, lineWidth) {
              setFont(px);
              const words = (s) => s.length ? s.split(/\s+/) : [''];
              let lines = [];

              for (const para of paragraphs) {
                let current = '';
                for (const w of words(para)) {
                  const trial = current ? current + ' ' + w : w;
                  if (__measureCtx.measureText(trial).width <= lineWidth) {
                    current = trial;
                  } else {
                    if (!current) {
                      // Word longer than width: force character cutting
                      let piece = '';
                      for (const ch of w) {
                        const t2 = piece + ch;
                        if (__measureCtx.measureText(t2).width <= lineWidth) piece = t2;
                        else {
                          if (piece) lines.push(piece);
                          piece = ch;
                        }
                      }
                      if (piece) {
                        current = piece;
                      } else {
                        current = '';
                      }
                    } else {
                      lines.push(current);
                      current = w;
                    }
                  }
                }
                lines.push(current);
              }
              return lines;
            }

            function fits(px) {
              const lines = wrapParagraph(px, innerW);
              if (!lines.length) return true;

              const perLine = px * lineHeight;
              const totalH = perLine * lines.length;

              for (const line of lines) {
                const w = __measureCtx.measureText(line).width;
                if (w > innerW + 0.5) return false;
              }
              return totalH <= innerH + 0.5;
            }

            let lo = min;
            let hi = Math.min(max, clamp);
            let ans = lo;
            while (lo <= hi) {
              const mid = Math.floor((lo + hi) / 2);
              if (fits(mid)) {
                ans = mid; lo = mid + 1;
              } else {
                hi = mid - 1;
              }
            }
            return ans;
          },

          async processSelection(imageSelection, previewImage, isBatch = false, skipImageEdit = false, skipOcrFontSize = false, hintText = null, providedStyle = null) {
            if (!this.selectedImage) return null;

            return new Promise((resolve) => {
                // Create a temporary canvas to crop the selected region
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                const img = new Image();

                img.onload = () => {
                    // Validate image dimensions
                    if (imageSelection.width <= 0 || imageSelection.height <= 0) {
                        alert('Please make a proper selection before removing an area.');
                        resolve(null);
                        return;
                    }

                    // Ensure selection is within the image boundaries
                    const clippedSelection = {
                        left: Math.max(0, Math.min(imageSelection.left, img.width)),
                        top: Math.max(0, Math.min(imageSelection.top, img.height)),
                        width: Math.min(imageSelection.width, img.width - Math.max(0, imageSelection.left)),
                        height: Math.min(imageSelection.height, img.height - Math.max(0, imageSelection.top))
                    };

                    // Calculate padded selection (40% padding on each side for more context)
                    const padding = 0.4; // 40% padding
                    const paddedWidth = Math.round(clippedSelection.width * (1 + padding * 2));
                    const paddedHeight = Math.round(clippedSelection.height * (1 + padding * 2));
                    
                    // Calculate padding while keeping the selection centered
                    const paddingX = Math.round(clippedSelection.width * padding);
                    const paddingY = Math.round(clippedSelection.height * padding);
                    
                    // Calculate the padded coordinates while keeping the selection centered
                    const paddedLeft = Math.max(0, Math.round(clippedSelection.left - paddingX));
                    const paddedTop = Math.max(0, Math.round(clippedSelection.top - paddingY));
                    
                    // Adjust if padding would go beyond image boundaries
                    const adjustedPaddedLeft = Math.min(paddedLeft, Math.max(0, img.width - paddedWidth));
                    const adjustedPaddedTop = Math.min(paddedTop, Math.max(0, img.height - paddedHeight));

                    // Calculate the actual padding used after boundary adjustments
                    const actualPaddingX = Math.round(clippedSelection.left - adjustedPaddedLeft);
                    const actualPaddingY = Math.round(clippedSelection.top - adjustedPaddedTop);

                    // Set canvas size to match padded selection
                    canvas.width = paddedWidth;
                    canvas.height = paddedHeight;

                    // Clear the canvas with transparency
                    ctx.clearRect(0, 0, paddedWidth, paddedHeight);

                    // Draw the padded region
                    ctx.drawImage(
                        img,
                        adjustedPaddedLeft,
                        adjustedPaddedTop,
                        paddedWidth,
                        paddedHeight,
                        0,
                        0,
                        paddedWidth,
                        paddedHeight
                    );

                    // Create a metadata object to better describe the task to the AI
                    const selectionMetadata = {
                        originalWidth: img.width,
                        originalHeight: img.height,
                        selectionX: clippedSelection.left - adjustedPaddedLeft,
                        selectionY: clippedSelection.top - adjustedPaddedTop,
                        selectionWidth: clippedSelection.width,
                        selectionHeight: clippedSelection.height,
                        paddedWidth: paddedWidth,
                        paddedHeight: paddedHeight
                    };

                    // Draw a subtle highlight around the selection area to help the AI
                    // (This will be removed in the final result)
                    const highlightCtx = canvas.getContext('2d');
                    highlightCtx.strokeStyle = 'rgba(255, 255, 255, 0.3)';  
                    highlightCtx.lineWidth = 1;
                    highlightCtx.strokeRect(
                        actualPaddingX, 
                        actualPaddingY, 
                        clippedSelection.width, 
                        clippedSelection.height
                    );

                    // Convert canvas to blob
                    canvas.toBlob(async (blob) => {
                        if (!blob) {
                            alert('Failed to process image selection. Please try again.');
                            resolve(null);
                            return;
                        }

                        // Validate dimensions
                        if (paddedWidth <= 0 || paddedHeight <= 0 || 
                            !Number.isInteger(paddedWidth) || !Number.isInteger(paddedHeight)) {
                            alert('Invalid image dimensions. Please try again.');
                            resolve(null);
                            return;
                        }

                        try {
                            // Generate preview for this specific selection
                            const selectionCanvas = document.createElement('canvas');
                            const selectionCtx = selectionCanvas.getContext('2d');
                            selectionCanvas.width = clippedSelection.width;
                            selectionCanvas.height = clippedSelection.height;

                            selectionCtx.drawImage(
                                img,
                                clippedSelection.left,
                                clippedSelection.top,
                                clippedSelection.width,
                                clippedSelection.height,
                                0,
                                0,
                                clippedSelection.width,
                                clippedSelection.height
                            );

                            let selectionPreviewImage = selectionCanvas.toDataURL('image/png');

                            // Detect if text is likely vertical (height significantly greater than width)
                            // If so, rotate the image 90° clockwise before OCR for better recognition
                            const isVerticalText = clippedSelection.height > clippedSelection.width * 1.5;
                            let rotationApplied = 0;

                            if (isVerticalText) {
                                // Create a rotated canvas for vertical text
                                const rotatedCanvas = document.createElement('canvas');
                                const rotatedCtx = rotatedCanvas.getContext('2d');

                                // Swap dimensions for 90° rotation
                                rotatedCanvas.width = selectionCanvas.height;
                                rotatedCanvas.height = selectionCanvas.width;

                                // Rotate 90° clockwise - this makes text that reads bottom-to-top
                                // (common for vertical product labels) become horizontal left-to-right
                                rotatedCtx.translate(rotatedCanvas.width, 0);
                                rotatedCtx.rotate(Math.PI / 2);
                                rotatedCtx.drawImage(selectionCanvas, 0, 0);

                                selectionPreviewImage = rotatedCanvas.toDataURL('image/png');
                                rotationApplied = 90; // Track that we rotated +90° (CW)
                            }

                            const aiRecognizeTextFormData = new FormData();
                            aiRecognizeTextFormData.append('image', selectionPreviewImage);

                            const aiRecognizeFontFormData = new FormData();
                            aiRecognizeFontFormData.append('image', selectionPreviewImage);
                            aiRecognizeFontFormData.append('fonts', window.fontFamilyManager.fonts.map(font => font.index).join(', '));

                            // Run text recognition (required) and font recognition (optional) simultaneously
                            // Font recognition can fail without breaking the flow
                            const [recognizeTextResponse, recognizeFontResponse] = await Promise.all([
                                axios.post('/ai-recognize-text-in-image', aiRecognizeTextFormData, {
                                    headers: { 'Content-Type': 'multipart/form-data' }
                                }),
                                (providedStyle && providedStyle.font_normal && providedStyle.font_bold)
                                     ? Promise.resolve({ data: { font_normal: providedStyle.font_normal, font_bold: providedStyle.font_bold } })
                                     : axios.post('/ai-recognize-font-in-image', aiRecognizeFontFormData, {
                                         headers: { 'Content-Type': 'multipart/form-data' }
                                     }).catch(err => {
                                         console.warn('Font recognition failed, using defaults:', err.message);
                                         return { data: { font_normal: null, font_bold: null } };
                                     })
                            ]);

                            const newTextFontNormal = recognizeFontResponse.data.font_normal || 'Arial';
                            const newTextFontBold = recognizeFontResponse.data.font_bold || 'Arial';

                            const newTextElementContent = hintText
                                ? hintText
                                : (recognizeTextResponse.data.text || '').replace(/<[^>]+>/g, '');
                            // Adjust angle: if we rotated the image for vertical text OCR,
                            // compensate by adding the rotation back to the detected angle
                            const detectedAngle = recognizeTextResponse.data.angle || 0;
                            const newTextElementAngle = detectedAngle - rotationApplied; // Subtract because we need to reverse the rotation

                            // For rotated vertical text, use "left" alignment by default
                            // since vertical text typically starts from the top (maps to "left" after -90° rotation)
                            const newTextElementAlignment = rotationApplied !== 0 ? 'left' : (recognizeTextResponse.data.alignment || 'left');
                            const newTextFontColor = providedStyle?.color || recognizeTextResponse.data.color || '#000000';
                            const newTextElementBackgroundColor = providedStyle?.background_color || recognizeTextResponse.data.background_color || '#ffffff';
                            const newTextFontWeight = providedStyle?.font_weight || 'normal';

                            // Store extracted text for AI prompt (if option is enabled)
                            if (this.showAiEditorAfterMakeEditable && newTextElementContent) {
                                this.extractedTextsForAiPrompt.push(newTextElementContent);
                            }

                            // Use captured coordinates if available (for consistent positioning after backup)
                            const coords = this._capturedTargetCoords || this.targetElement.aCoords;
                            const targetElementOffsetX = coords.tl.x;
                            const targetElementOffsetY = coords.tl.y;
                            const targetElementWidth = coords.br.x - coords.tl.x;
                            const targetElementHeight = coords.br.y - coords.tl.y;
                            const targetElementScaleX = targetElementWidth / img.width;
                            const targetElementScaleY = targetElementHeight / img.height;
                            const imageScale = Math.max(targetElementScaleX, targetElementScaleY) || 1;

                            // Use the full selection area for text placement (not OCR-adjusted contentBox)
                            // This ensures text fills the area the user selected
                            const boxLeftInImage  = clippedSelection.left;
                            const boxTopInImage   = clippedSelection.top;
                            const boxWidthInImage = clippedSelection.width;
                            const boxHeightInImage = clippedSelection.height;

                            // Map to target canvas coordinates
                            let newTextElementOffsetX = targetElementOffsetX + (boxLeftInImage * targetElementScaleX);
                            let newTextElementOffsetY = targetElementOffsetY + (boxTopInImage  * targetElementScaleY);
                            let newTextElementWidth   = boxWidthInImage  * targetElementScaleX;
                            let newTextElementHeight  = boxHeightInImage * targetElementScaleY;

                            // For vertical text (rotated), swap width and height
                            // This is because in Fabric.js, the "width" property controls text wrapping,
                            // but after rotation, the visual width is actually the original height
                            if (rotationApplied !== 0) {
                                const tempWidth = newTextElementWidth;
                                newTextElementWidth = newTextElementHeight;
                                newTextElementHeight = tempWidth;
                            }

                            let fontFamilyNormal;
                            let fontFamilyBold;
                            if (detectedAngle !== 0) {
                                // For rotated text, font detection is very bad, so we default to Arial.
                                fontFamilyNormal = 'Arial, sans-serif';
                                fontFamilyBold = 'Arial, sans-serif';
                            } else {
                                // Ensure font family is properly formatted (declared before use)
                                fontFamilyNormal = newTextFontNormal && newTextFontNormal.trim() ? newTextFontNormal.trim() : 'Arial, sans-serif';
                                fontFamilyBold = newTextFontBold && newTextFontBold.trim() ? newTextFontBold.trim() : 'Arial, sans-serif';
                            }

                            let fontSize;

                            if (providedStyle && providedStyle.font_size) {
                                fontSize = providedStyle.font_size;
                            } else if (!skipOcrFontSize) {
                                // Estimate font size from OCR line heights (actual text size in image)
                                // This preserves the original font size from the image.
                                // Skipped for auto-detected text blocks (GPT-4o bounding boxes) because
                                // Tesseract fails on small/stylized crops and returns inaccurate heights.
                                try {
                                    const lineBoxes = await this.ocrLineBoxesFromDataURL(selectionPreviewImage);
                                    const ocrFontSize = this.estimateFontSizeFromLineBoxes(lineBoxes);

                                    if (ocrFontSize && ocrFontSize > 0) {
                                        fontSize = Math.round(ocrFontSize * imageScale);
                                    }
                                } catch (e) {
                                    console.warn('OCR font size estimation failed:', e);
                                }
                            }

                            // Fallback to fitting algorithm if OCR estimation fails
                            if (!fontSize || fontSize < 8) {
                                fontSize = this.computeFittingFontSize({
                                    text: newTextElementContent,
                                    fontFamily: fontFamilyNormal,
                                    boxW: newTextElementWidth,
                                    boxH: newTextElementHeight,
                                    min: 8,
                                    max: 300,
                                    paddingRatio: 0,
                                    lineHeight: 1.15,
                                    clamp: 300
                                });
                            }

                            // Apply 30% reduction as requested by user to match original aesthetic better
                            // if (fontSize) {
                            //     fontSize = Math.round(fontSize * 0.7);
                            // }

                            // Adjust positioning based on rotation
                            if (rotationApplied !== 0) {
                                // For -90° rotated text, we need special position calculation
                                // Fabric.js rotates around the top-left origin by default
                                //
                                // For a textbox with dimensions (W, H) at position (L, T) rotated -90°:
                                // - Visual top-left moves to (L, T - W)
                                // - Visual center is at (L + H/2, T - W/2)
                                //
                                // After our width/height swap:
                                // - newTextElementWidth = original selection height (becomes visual height after rotation)
                                // - newTextElementHeight = original selection width (becomes visual width after rotation)

                                // Calculate the center of the original selection (in canvas coordinates)
                                const originalSelectionCenterX = targetElementOffsetX + ((boxLeftInImage + boxWidthInImage / 2) * targetElementScaleX);
                                const originalSelectionCenterY = targetElementOffsetY + ((boxTopInImage + boxHeightInImage / 2) * targetElementScaleY);

                                // To place visual center at selection center:
                                // L + H/2 = cx  =>  L = cx - H/2  =>  L = cx - newTextElementHeight/2
                                // T - W/2 = cy  =>  T = cy + W/2  =>  T = cy + newTextElementWidth/2
                                newTextElementOffsetX = originalSelectionCenterX - (newTextElementHeight / 2);
                                newTextElementOffsetY = originalSelectionCenterY + (newTextElementWidth / 2);
                            } else {
                                 // For non-rotated text, use precise positioning
                                 // No arbitrary offsets to prevent drift
                             }

                            const { text, styles } = parseText(newTextElementContent, newTextFontColor);

                            const rawText = recognizeTextResponse.data.text || '';
                            const rawLines = rawText.split('\n');
                            const plainLines = text.split('\n');

                            let isBold = false;

                            for (let lineIndex = 0; lineIndex < rawLines.length && lineIndex < plainLines.length; lineIndex++) {
                              const rawLine = rawLines[lineIndex];
                              const plainLine = plainLines[lineIndex];
                              let plainCharIndex = 0;
                              isBold = false;

                              for (let i = 0; i < rawLine.length && plainCharIndex < plainLine.length; ) {
                                if (rawLine.startsWith('<b>', i)) {
                                  isBold = true;
                                  i += 3;
                                  continue;
                                }

                                if (rawLine.startsWith('</b>', i)) {
                                  isBold = false;
                                  i += 4;
                                  continue;
                                }

                                if (isBold && styles[lineIndex] && styles[lineIndex][plainCharIndex]) {
                                  styles[lineIndex][plainCharIndex].fontFamily = fontFamilyBold;
                                }

                                plainCharIndex++;
                                i++;
                              }
                            }

                            // canvasEditModeControl.setKeepSelected(false);
                            // Helper function to add text field with font fallback
                            const addTextFieldWithFallback = async (fontToUse, boldFontToUse) => {
                                // Update styles with the font being used
                                const updatedStyles = JSON.parse(JSON.stringify(styles));
                                if (fontToUse !== fontFamilyNormal) {
                                    // If using fallback font, update all styles
                                    for (const lineIndex in updatedStyles) {
                                        for (const charIndex in updatedStyles[lineIndex]) {
                                            if (updatedStyles[lineIndex][charIndex].fontFamily === fontFamilyBold) {
                                                updatedStyles[lineIndex][charIndex].fontFamily = boldFontToUse;
                                            }
                                        }
                                    }
                                }

                                return addAddField("Multi Line Text", this.instanceId, {
                                    text: text,
                                    angle: newTextElementAngle,
                                    _angle: newTextElementAngle,
                                    _styles: updatedStyles,
                                    color: newTextFontColor,
                                    fontColor: newTextFontColor,
                                    font_color: newTextFontColor,
                                    _color: newTextFontColor,
                                    _fontColor: newTextFontColor,
                                    _font_color: newTextFontColor,
                                    font: fontToUse,
                                    fontFamily: fontToUse,
                                    font_family: fontToUse,
                                    _font: fontToUse,
                                    _fontFamily: fontToUse,
                                    _font_family: fontToUse,
                                    size: fontSize,
                                    fontSize: fontSize,
                                    fontsize: fontSize,
                                    font_size: fontSize,
                                    _size: fontSize,
                                    _fontSize: fontSize,
                                    _fontsize: fontSize,
                                    _font_size: fontSize,
                                    alignment: newTextElementAlignment,
                                    textAlign: newTextElementAlignment,
                                    text_align: newTextElementAlignment,
                                    _alignment: newTextElementAlignment,
                                    _textAlign: newTextElementAlignment,
                                    _text_align: newTextElementAlignment,
                                    offset_x: newTextElementOffsetX,
                                    offset_y: newTextElementOffsetY,
                                    _offset_x: newTextElementOffsetX,
                                    _offset_y: newTextElementOffsetY,
                                    height: newTextElementHeight,
                                    width: newTextElementWidth,
                                    _height: newTextElementHeight,
                                    _width: newTextElementWidth,
                                    fontWeight: newTextFontWeight,
                                    font_weight: newTextFontWeight,
                                    _fontWeight: newTextFontWeight
                                });
                            };

                            // Try with recognized font first, fall back to Arial if it fails
                            let createdField = null;
                            try {
                                createdField = await addTextFieldWithFallback(fontFamilyNormal, fontFamilyBold);
                            } catch (fontError) {
                                console.warn('Font loading failed for', fontFamilyNormal, '- retrying with Arial:', fontError.message);
                                createdField = await addTextFieldWithFallback('Arial', 'Arial');
                            }
                            // canvasEditModeControl.setKeepSelected(true);

                            if (!this.removeTextFromImage || skipImageEdit) {
                                resolve({
                                    text: newTextElementContent,
                                    createdField,
                                    style: {
                                        font_normal: newTextFontNormal,
                                        font_bold: newTextFontBold,
                                        color: newTextFontColor,
                                        background_color: newTextElementBackgroundColor,
                                        font_weight: newTextFontWeight,
                                        font_size: fontSize
                                    }
                                });
                                return;
                            }

                            this.targetElement.canvas.setActiveObject(this.targetElement);
                            this.targetElement.canvas.renderAll();

                            // Prepare payload for AI edit
                            const aiEditFormData = new FormData();
                            const file = new File([blob], 'selected_region.png', { type: blob.type });
                            aiEditFormData.append('image', file);
                            aiEditFormData.append('width', paddedWidth.toString());
                            aiEditFormData.append('height', paddedHeight.toString());
                            aiEditFormData.append('originalSelection', JSON.stringify({
                                left: actualPaddingX,
                                top: actualPaddingY,
                                width: Math.round(clippedSelection.width),
                                height: Math.round(clippedSelection.height),
                                metadata: selectionMetadata
                            }));

                            // Use the selected infill method
                            if (['gemini_nano_banana_pro', 'gemini_nano_banana_2', 'gpt-image-2'].includes(this.infillMethod)) {
                                await this.identifyTextElements();
                                // Use AI infill with Nano Banana Pro (Gemini 3 Pro)
                                aiEditFormData.append('infillMethod', this.infillMethod);
                                // If we have identified text elements, pass them; otherwise, send a description of the recognized text
                                if (this.textElementsInImage.length > 0) {
                                    const removalData = this.textElementsInImage.map(text => {
                                        const block = this.detectedTextBlocksMap[text];
                                        return {
                                            text: text,
                                            box: block ? block.box_2d : null
                                        };
                                    });
                                    aiEditFormData.append('elementsToRemove', JSON.stringify(removalData));
                                } else {
                                    // Fallback: use the recognized text content to identify what to remove
                                    aiEditFormData.append('elementsToRemove', JSON.stringify([
                                        'The text "' + newTextElementContent + '" in the center of the image'
                                    ]));
                                }
                            } else if (this.fillBackgroundInsteadOfAiEdit === true) {
                                // Legacy AI edit path
                                aiEditFormData.append('promptForEditing', 'Remove the text "' + newTextElementContent + '" from this image, while preserving the ' + newTextElementBackgroundColor + ' background.');
                            } else {
                                // Solid color fill (default)
                                aiEditFormData.append('infillMethod', 'solid_color');
                                aiEditFormData.append('fillColor', newTextElementBackgroundColor);
                            }

                            const editImageResponse = await axios.post('/ai-edit-image', aiEditFormData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                            });

                            if (!editImageResponse.data || !editImageResponse.data.url) {
                                throw new Error('Invalid response from server');
                            }

                            resolve({
                                type: 'ai',
                                editedUrl: editImageResponse.data.url,
                                clippedSelection,
                                actualPaddingX,
                                actualPaddingY,
                                createdField,
                                style: {
                                    font_normal: newTextFontNormal,
                                    font_bold: newTextFontBold,
                                    color: newTextFontColor
                                }
                            });
                        } catch (error) {
                            console.error('Error processing selection:', error);
                            alert('There was an error processing the image. Please try again.');
                            resolve(null);
                        }
                    }, 'image/png');
                };

                img.onerror = () => {
                    console.error('Failed to load image');
                    alert('Failed to load the image. Please try again.');
                    resolve(null);
                };

                img.src = this.selectedImage;
            });
          },

          async ensureTesseract() {
            if (this.tessWorker) return this.tessWorker;

            const worker = await createWorker();

            // Compatibility with different tesseract.js versions
            if (typeof worker.load === 'function') {
              await worker.load();
            }

            if (typeof worker.loadLanguage === 'function' && typeof worker.initialize === 'function') {
              // Classic API (v2–v5)
              await worker.loadLanguage('eng');
              await worker.initialize('eng');
            } else if (typeof worker.initialize === 'function' && typeof worker.loadLanguage !== 'function') {
              // Some modern builds accept the language directly after load()
              await worker.initialize('eng');
            } else if (typeof worker.reinitialize === 'function') {
              // Other builds require reinitialize
              await worker.reinitialize('eng');
            } else {
              throw new Error('Unsupported tesseract.js worker API');
            }

            this.tessWorker = worker;
            return worker;
          },

          unionBBoxFromLines(lines) {
            if (!lines || !lines.length) return null;
            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
            for (const l of lines) {
              const x0 = l.x, y0 = l.y, x1 = l.x + l.w, y1 = l.y + l.h;
              if (x0 < minX) minX = x0;
              if (y0 < minY) minY = y0;
              if (x1 > maxX) maxX = x1;
              if (y1 > maxY) maxY = y1;
            }
            return { x: Math.max(0, minX), y: Math.max(0, minY), w: Math.max(0, maxX - minX), h: Math.max(0, maxY - minY) };
          },

          async ocrLineBoxesFromDataURL(dataUrl) {
            const worker = await this.ensureTesseract();
            const { data } = await worker.recognize(dataUrl);
            // Returns line boxes with usable height
            const lines = (data?.lines ?? []).map(l => ({
              text: (l.text || '').trim(),
              x: l.bbox?.x0 ?? 0,
              y: l.bbox?.y0 ?? 0,
              w: (l.bbox ? (l.bbox.x1 - l.bbox.x0) : 0),
              h: (l.bbox ? (l.bbox.y1 - l.bbox.y0) : 0)
            })).filter(l => l.text && l.w > 0 && l.h > 0);
            return lines;
          },

          estimateFontSizeFromLineBoxes(lines) {
            if (!lines.length) return null;
            // median line height as a proxy of line-height
            const hs = lines.map(l => l.h).sort((a,b)=>a-b);
            const hMed = hs[Math.floor(hs.length/2)];
            // line-height ≈ 1.18 * fontSize  => fontSize ≈ hMed / 1.18
            const fs = Math.max(10, Math.round(hMed / 1.18));
            return fs;
          },

          async applyImageEditsSequentially(edits) {
            // Apply all edit descriptors sequentially onto the current selectedImage
            const loadImage = (src) =>
                new Promise((res, rej) => {
                    const im = new Image();
                    im.crossOrigin = 'anonymous';
                    im.onload = () => res(im);
                    im.onerror = rej;
                    im.src = src;
                });

            const baseImg = await loadImage(this.selectedImage);

            const fullCanvas = document.createElement('canvas');
            const fullCtx = fullCanvas.getContext('2d');
            fullCanvas.width = baseImg.width;
            fullCanvas.height = baseImg.height;

            // Start with the current image
            fullCtx.drawImage(baseImg, 0, 0);

            for (const edit of edits) {
                if (edit.type === 'fill') {
                    // Fill the selected area with the background color
                    fullCtx.fillStyle = edit.backgroundColor || '#ffffff';
                    fullCtx.fillRect(
                        edit.clippedSelection.left,
                        edit.clippedSelection.top,
                        edit.clippedSelection.width,
                        edit.clippedSelection.height
                    );
                } else if (edit.type === 'ai') {
                    const editedImg = await loadImage(
                        edit.editedUrl.startsWith('http') ? edit.editedUrl : (window.location.origin + edit.editedUrl)
                    );

                    // Create a mask from the current canvas region to preserve alpha
                    const maskCanvas = document.createElement('canvas');
                    const maskCtx = maskCanvas.getContext('2d');
                    maskCanvas.width = edit.clippedSelection.width;
                    maskCanvas.height = edit.clippedSelection.height;

                    // Draw the current selection region
                    maskCtx.drawImage(
                        fullCanvas,
                        edit.clippedSelection.left,
                        edit.clippedSelection.top,
                        edit.clippedSelection.width,
                        edit.clippedSelection.height,
                        0,
                        0,
                        edit.clippedSelection.width,
                        edit.clippedSelection.height
                    );

                    const maskData = maskCtx.getImageData(0, 0, edit.clippedSelection.width, edit.clippedSelection.height);
                    const maskPixels = maskData.data;

                    // Draw the edited crop into a temp canvas and copy alpha from mask
                    const editedCanvas = document.createElement('canvas');
                    const editedCtx = editedCanvas.getContext('2d');
                    editedCanvas.width = edit.clippedSelection.width;
                    editedCanvas.height = edit.clippedSelection.height;

                    editedCtx.drawImage(
                        editedImg,
                        edit.actualPaddingX,
                        edit.actualPaddingY,
                        edit.clippedSelection.width,
                        edit.clippedSelection.height,
                        0,
                        0,
                        edit.clippedSelection.width,
                        edit.clippedSelection.height
                    );

                    const editedData = editedCtx.getImageData(0, 0, edit.clippedSelection.width, edit.clippedSelection.height);
                    const editedPixels = editedData.data;

                    for (let i = 0; i < maskPixels.length; i += 4) {
                        editedPixels[i + 3] = maskPixels[i + 3]; // Copy alpha channel
                    }
                    editedCtx.putImageData(editedData, 0, 0);

                    // Composite back onto the full canvas
                    fullCtx.drawImage(
                        editedCanvas,
                        0,
                        0,
                        edit.clippedSelection.width,
                        edit.clippedSelection.height,
                        edit.clippedSelection.left,
                        edit.clippedSelection.top,
                        edit.clippedSelection.width,
                        edit.clippedSelection.height
                    );
                }
            }

            const editedImageUrl = fullCanvas.toDataURL('image/png');
            this.selectedImage = editedImageUrl;
            this.editedImageUrl = editedImageUrl;
          },

          async handleSave() {
              if (!this.editedImageUrl) {
                  alert('No changes to save. Please remove text first.');
                  return;
              }

              if (!this.targetElement) {
                  alert('No target element selected.');
                  return;
              }

              $('.loadingOverlay').fadeIn();
              $('.loadingOverlay-text').html('Saving edited image...');

              try {
                  // Convert base64 data URL to blob
                  const response = await fetch(this.editedImageUrl);
                  const blob = await response.blob();

                  const now = new Date();
                  const timestamp = now.toISOString().replace(/[:.]/g, "-");
                  const filename = `edited-image-${timestamp}.png`;
                  const file = new File([blob], filename, { type: 'image/png' });

                  // Upload the image directly to the server
                  const formData = new FormData();
                  formData.append('file', file);
                  formData.append('url', URL.createObjectURL(file));

                  const uploadResponse = await axios.post('/banner/store_remote_image', formData, {
                      headers: { 'Content-Type': 'multipart/form-data' }
                  });

                  const newImageUrl = uploadResponse.data;

                  if (!newImageUrl) {
                      throw new Error('Failed to upload image - no URL returned');
                  }

                  // Build the share URL for the uploaded image
                  const shareUrl = `/share?file=${newImageUrl}`;

                  // Save original dimensions and position BEFORE changing source
                  const originalProps = {
                      left: this.targetElement.left,
                      top: this.targetElement.top,
                      angle: this.targetElement.angle,
                      displayWidth: this.targetElement.getScaledWidth(),
                      displayHeight: this.targetElement.getScaledHeight(),
                  };

                  // Update the target element's source directly
                  await new Promise((resolve, reject) => {
                      this.targetElement.setSrc(shareUrl, (img) => {
                          if (img) {
                              // Calculate new scale to maintain the same visual size
                              const newScaleX = originalProps.displayWidth / this.targetElement.width;
                              const newScaleY = originalProps.displayHeight / this.targetElement.height;

                              this.targetElement.set({
                                  left: originalProps.left,
                                  top: originalProps.top,
                                  scaleX: newScaleX,
                                  scaleY: newScaleY,
                                  angle: originalProps.angle,
                              });

                              this.targetElement.setCoords();
                              this.targetElement.canvas.renderAll();
                              resolve();
                          } else {
                              reject(new Error('Failed to set image source'));
                          }
                      }, { crossOrigin: 'anonymous' });
                  });

                  // Update layout data - combine correct scale calculation with explicit URL save
                  const { updateLayoutChanges, canvas_data, getChangesLayoutById } = getShowPageScope();

                  if (updateLayoutChanges && canvas_data && this.targetElement.id && this.instanceId) {
                      const idParts = this.targetElement.id.split('_');
                      const index = parseInt(idParts[idParts.length - 1], 10);

                      // Detect if this is an additional background image or regular background image
                      const isAdditionalBgImage = this.targetElement.id.startsWith('add_bk_theme_img_');
                      const isRegularBgImage = this.targetElement.id.startsWith('bk_theme_img_');

                      if (!isNaN(index)) {
                          // Get merged data
                          const mergedData = {
                              ...canvas_data[this.instanceId]?._data,
                              ...(getChangesLayoutById ? getChangesLayoutById(this.instanceId) : {})
                          };

                          // Get the new image URL
                          const newImageUrl = this.targetElement.getSrc();

                          if (isAdditionalBgImage) {
                              // Update additional_background_images array
                              const additional_background_images = [...(mergedData.additional_background_images || [])];
                              additional_background_images[index] = newImageUrl;
                              updateLayoutChanges(this.instanceId, 'additional_background_images', additional_background_images);

                              // Update add_bk_img_width and add_bk_img_height (critical for correct sizing)
                              const add_bk_img_width = [...(mergedData.add_bk_img_width || [])];
                              const add_bk_img_height = [...(mergedData.add_bk_img_height || [])];
                              add_bk_img_width[index] = originalProps.displayWidth;
                              add_bk_img_height[index] = originalProps.displayHeight;
                              updateLayoutChanges(this.instanceId, 'add_bk_img_width', add_bk_img_width);
                              updateLayoutChanges(this.instanceId, 'add_bk_img_height', add_bk_img_height);

                              // Update add_bk_img_* scale values - use 1 since we're setting exact dimensions
                              const add_bk_img_scale = [...(mergedData.add_bk_img_scale || [])];
                              const add_bk_img_scale_y = [...(mergedData.add_bk_img_scale_y || [])];
                              add_bk_img_scale[index] = 1;
                              add_bk_img_scale_y[index] = 1;
                              updateLayoutChanges(this.instanceId, 'add_bk_img_scale', add_bk_img_scale);
                              updateLayoutChanges(this.instanceId, 'add_bk_img_scale_y', add_bk_img_scale_y);

                              // Set object_fill to ensure dimensions are used (required by loading code)
                              // Use objectFillCrop: true to prevent repositioning
                              const add_bk_img_object_fill = [...(mergedData.add_bk_img_object_fill || [])];
                              add_bk_img_object_fill[index] = {
                                  width_to_fill: Math.round(originalProps.displayWidth),
                                  height_to_fill: Math.round(originalProps.displayHeight),
                                  align_to_fill: 'center,center',
                                  objectFillCrop: true,
                              };
                              updateLayoutChanges(this.instanceId, 'add_bk_img_object_fill', add_bk_img_object_fill);

                              // Keep existing offset values
                              const add_bk_img_offset_x = [...(mergedData.add_bk_img_offset_x || [])];
                              const add_bk_img_offset_y = [...(mergedData.add_bk_img_offset_y || [])];
                              updateLayoutChanges(this.instanceId, 'add_bk_img_offset_x', add_bk_img_offset_x);
                              updateLayoutChanges(this.instanceId, 'add_bk_img_offset_y', add_bk_img_offset_y);

                          } else {
                              // Regular background image - update background array
                              const bgImageInfo = canvas_data[this.instanceId]?.background_theme_image?.[index];
                              const dimensionWidth = bgImageInfo?.width || originalProps.displayWidth;
                              const dimensionHeight = bgImageInfo?.height || originalProps.displayHeight;

                              const targetBkImgScale = originalProps.displayWidth / dimensionWidth;
                              const targetBkImgScaleY = originalProps.displayHeight / dimensionHeight;

                              // 1. Background URL
                              const background = [...(mergedData.background || [])];
                              background[index] = newImageUrl;
                              updateLayoutChanges(this.instanceId, 'background', background);

                              // 2. Scale values
                              const bk_img_scale = [...(mergedData.bk_img_scale || [])];
                              const bk_img_scale_y = [...(mergedData.bk_img_scale_y || [])];
                              bk_img_scale[index] = targetBkImgScale;
                              bk_img_scale_y[index] = targetBkImgScaleY;
                              updateLayoutChanges(this.instanceId, 'bk_img_scale', bk_img_scale);
                              updateLayoutChanges(this.instanceId, 'bk_img_scale_y', bk_img_scale_y);

                              // 3. Dimensions
                              const bk_img_width = [...(mergedData.bk_img_width || [])];
                              const bk_img_height = [...(mergedData.bk_img_height || [])];
                              bk_img_width[index] = originalProps.displayWidth;
                              bk_img_height[index] = originalProps.displayHeight;
                              updateLayoutChanges(this.instanceId, 'bk_img_width', bk_img_width);
                              updateLayoutChanges(this.instanceId, 'bk_img_height', bk_img_height);

                              // 4. Position offsets (keep existing values)
                              const bk_img_offset_x = [...(mergedData.bk_img_offset_x || [])];
                              const bk_img_offset_y = [...(mergedData.bk_img_offset_y || [])];
                              updateLayoutChanges(this.instanceId, 'bk_img_offset_x', bk_img_offset_x);
                              updateLayoutChanges(this.instanceId, 'bk_img_offset_y', bk_img_offset_y);

                          }

                          // Force save immediately to persist the changes
                          const { saveLayout } = getShowPageScope();
                          if (saveLayout) {
                              await saveLayout(false, true, 'Saving edited image...');
                          }
                      }
                  }

                  await this.saveImageToLayoutsFolder(shareUrl);

                  $('.loadingOverlay').fadeOut();
                  toastr.success('Image updated and saved successfully');

              } catch (error) {
                  console.error('Error saving edited image:', error);
                  $('.loadingOverlay').fadeOut();
                  toastr.error('Failed to save edited image: ' + error.message);
                  throw error;
              }
          },
          async checkLinkedFolderExists() {
              try {
                  const response = await Requests.checkLinkedFolderExists('/file/check_linked_folder_exists', {
                      layout_id: this.layoutId,
                  });
                  
                  return response.data.success ? response.data.folder : null;
              } catch (error) {
                  console.error('Error checking linked folder:', error);
                  return null;
              }
          },
          async checkRootFolderExists(folderName) {
              try {
                  const { data } = await Requests.checkRootFolderExists(folderName);

                  return data.exists ? data.folder : null;
              } catch (error) {
                  console.error('Error checking folder by name:', error);
                  return null;
              }
          },
          async createFolder(folderName) {
              try {
                  const response = await Requests.createFolderByName('/file/create_folder_by_name', {
                      parent_id: null,
                      layout_id: this.layoutId,
                      name: folderName,
                  });

                  return response.data.id;
              } catch (error) {
                  console.error('Error creating folder:', error);
                  throw new Error('Failed to create folder');
              }
          },
          async saveImageToLayoutsFolder(imageUrl) {
              try {
                  const folder = await this.checkRootFolderExists(this.rootFolderName);
                  const folderId = folder ? folder.id : await this.createFolder(this.rootFolderName);

                  const response = await fetch(imageUrl);
                  const blob = await response.blob();
                  const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
                  const file = new File([blob], `image-${timestamp}.png`, { type: 'image/png' });

                  const uploadResponse = await uploadFolderFile([file], folderId);
                  const fileData = uploadResponse[0];

                  if (fileData && !fileData.isExist) {
                      const { uploadId, key, data, uploadedFolder, fileName: uploadedFileName, thumbnail, id } = fileData;

                      const signedUrlResponse = await axios.get(`/file/upload_folder_file/${uploadId}/1?key=${key}`);
                      const { url } = signedUrlResponse.data;

                      if (url) {
                          await uploadFile(url, file);

                          const partsResponse = await axios.get(`/file/upload_folder_file/${uploadId}?key=${key}`);

                          if (partsResponse.data.parts) {
                              await axios.post(`/file/upload_folder_file/${uploadId}/complete`, {
                                  data,
                                  fileName: uploadedFileName,
                                  id,
                                  isExist: false,
                                  key,
                                  uploadedFolder,
                                  parts: partsResponse.data.parts,
                                  thumbnail,
                              });
                          }
                      }
                  }
              } catch (error) {
                  console.error('Error saving to Layouts folder:', error);
              }
          },
          handleUndo() {
              if (this.imageHistory.length > 0) {
                  this.selectedImage = this.imageHistory.pop();
                  this.resetSelection();
              }
          },
          selectArea(index) {
              this.currentSelectionIndex = index;
              const selection = this.selections[index];
              this.imageSelection = { ...selection.imageSelection };
              this.selectionBox = { ...selection.displayBox };
              this.previewImage = selection.previewImage;
              this.hasSelection = true;
          },

          drawSelections() {
              const canvas = this.$refs.selectionCanvas;
              if (!canvas) return;

              const imgRef = this.$refs.zoomedImage;
              if (!imgRef) return;

              // Size the canvas to exactly cover the rendered image area
              const imgRect = imgRef.getBoundingClientRect();
              const wrapperRect = canvas.parentElement.getBoundingClientRect();

              // The canvas covers the whole wrapper; the image may be offset inside it
              canvas.width = wrapperRect.width;
              canvas.height = wrapperRect.height;

              // Image offset from wrapper edge (due to flex centering)
              const ox = imgRect.left - wrapperRect.left;
              const oy = imgRect.top - wrapperRect.top;

              const ctx = canvas.getContext('2d');
              ctx.clearRect(0, 0, canvas.width, canvas.height);

              // Draw saved selections
              this.selections.forEach((sel, index) => {
                  const box = sel.displayBox;
                  const isActive = index === this.currentSelectionIndex;

                  ctx.save();
                  ctx.setLineDash([6, 4]);
                  ctx.lineWidth = 2;
                  ctx.strokeStyle = isActive ? '#ffcc00' : '#209c86';

                  // displayBox is image-relative, add ox/oy to get wrapper-relative
                  ctx.strokeRect(
                      box.left + ox,
                      box.top + oy,
                      box.width,
                      box.height
                  );

                  // Subtle fill for active
                  if (isActive) {
                      ctx.fillStyle = 'rgba(255, 204, 0, 0.08)';
                      ctx.fillRect(box.left + ox, box.top + oy, box.width, box.height);
                  }

                  ctx.restore();
              });

              // Draw the live selection being dragged
              if (this.isSelecting && this.selectionBox.width > 0 && this.selectionBox.height > 0) {
                  ctx.save();
                  ctx.setLineDash([6, 4]);
                  ctx.lineWidth = 2;
                  ctx.strokeStyle = '#209c86';
                  ctx.fillStyle = 'rgba(32, 156, 134, 0.08)';

                  const { left, top, width, height } = this.selectionBox;
                  ctx.strokeRect(left, top, width, height);
                  ctx.fillRect(left, top, width, height);
                  ctx.restore();
              }
          },

          removeSelection(index) {
              this.selections.splice(index, 1);
              if (this.currentSelectionIndex >= this.selections.length) {
                  this.currentSelectionIndex = this.selections.length - 1;
              }
              if (this.selections.length === 0) {
                  this.resetSelection();
              }
          },
          clearAllSelections() {
              this.selections = [];
              this.currentSelectionIndex = -1;
              this.resetSelection();
          },
          async triggerTranslation(targetFieldIds = []) {
              if (!this.translateAfterExtract || this.translateTargetLanguages.length === 0) {
                  return;
              }

              try {
                  const fieldIdsToTranslate = Array.isArray(targetFieldIds) && targetFieldIds.length > 0
                      ? [...new Set(targetFieldIds.filter(Boolean))]
                      : [...new Set(this.createdFieldIds.filter(Boolean))];

                  // Save layout first - the backend needs the text fields to be registered
                  const { saveLayout } = getShowPageScope();
                  await saveLayout(true, false, 'Preparing for translation...');

                  // Translate only newly created text fields from the current run
                  const preview = this.currentTemplateOption?.preview || this.fallbackPreview;
                  const textFields = preview?.canvas
                      ? preview.canvas
                          .getObjects('textbox')
                          .filter(obj => obj.visible !== false && obj.element_id)
                          .filter(obj => fieldIdsToTranslate.includes(obj.element_id))
                      : [];

                  if (textFields.length === 0) {
                      toastr.warning('No newly extracted text fields found to translate');
                      return;
                  }

                  // Determine handler based on number of languages
                  // Single language: translate in place
                  // Multiple languages: copy template for each additional language
                  const handler = this.translateTargetLanguages.length === 1
                      ? TRANSLATE_HANDLER_TYPES.TRANSLATE
                      : TRANSLATE_HANDLER_TYPES.COPY_AND_TRANSLATE;

                  const lang = this.translateTargetLanguages.length === 1
                      ? this.translateTargetLanguages[0]
                      : [...this.translateTargetLanguages];

                  toastr.info('Translating extracted text...');

                  // Fire translation event (will be handled by show.js listener)
                  editorControlEventBroker.fire(EVENTS.TRANSLATE, {
                      textFields,
                      multilineTextFields: [],
                      additionalTextFields: [],
                      additionalMultilineTextFields: [],
                      instance: preview,
                      lang,
                      handler
                  });

                  // Handle RTL languages after a delay to allow translation to complete
                  setTimeout(() => {
                      this.handleRTLLanguages(lang, fieldIdsToTranslate);
                  }, 2000);
              } catch (error) {
                  console.error('Error in triggerTranslation:', error);
                  toastr.error('Translation failed: ' + error.message);
              }
          },
          handleRTLLanguages(lang, targetElementIds = []) {
              const rtlLanguages = ['Arabic', 'Hebrew'];
              const isRTL = Array.isArray(lang)
                  ? lang.some(l => rtlLanguages.includes(l))
                  : rtlLanguages.includes(lang);

              if (isRTL) {
                  const preview = this.currentTemplateOption?.preview || this.fallbackPreview;
                  const canvas = preview?.canvas;
                  if (!canvas) return;

                  const targetIdsSet = new Set(targetElementIds);

                  canvas.getObjects('textbox').forEach(obj => {
                      if (obj.visible !== false && targetIdsSet.has(obj.element_id)) {
                          obj.set({ textAlign: 'right' });
                      }
                  });
                  canvas.requestRenderAll();
              }
          }
      }
  }
  </script>
  
  <style scoped lang="scss">
  .text-blocks-select-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .copy-text-blocks-header-btn {
    border: 0;
    background: transparent;
    padding: 2px 4px;
    color: #6c757d;
    line-height: 1;
    cursor: pointer;
  }

  .copy-text-blocks-header-btn:hover:not(:disabled) {
    color: #209c86;
  }

  .copy-text-blocks-header-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .chat-module {
      height: calc(100vh - 98px);
  }
  .chat-module .card {
      position: relative;
      overflow-y: auto;
  }
  .message {
      margin: 10px;
      padding: 10px;
      border-radius: 10px;
  }
  .message p {
      margin-bottom: 0px;
  }
  .message-user {
      background-color: #209c86;
      color: white;
      text-align: right;
  }
  .message-assistant {
      background-color: #e9ecef;
      text-align: left;
  }
  .image-container {
      position: relative;
      display: inline-block;
      text-align: center;
      overflow: hidden;
      margin: 0 auto;
      max-width: 100%;
  }
  .image-container-wrapper {
    border: 1px solid #209c86;
    flex-grow: 1;
    margin: auto;
    width: 100%;
    position: relative;
    cursor: crosshair;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .image-container.disabled {
      cursor: not-allowed;
      opacity: 0.7;
  }
  .selection-canvas {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 5;
  }
  

  
  .text-removal-container {
      width: calc(65vw - 9px);
      height: fit-content;
      position: fixed;
      background: rgb(255, 255, 255);
      z-index: 101;
      top: 87px;
      right: 64px;
      border: 1px solid;
      box-shadow: 2px 2px #888888;
      overflow-y: auto;
      padding: 20px;
      cursor: pointer;
  }
  
  .text-removal-content {
      cursor: default;
  }
  
  .text-removal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 15px;
  }
  
  .text-removal-title {
      margin: 0;
  }
  
  .text-removal-body {
      max-height: 50vh;
  }
  
  .text-removal-footer {
      display: flex;
      gap: 10px;
      margin-top: 15px;
      justify-content: flex-end;
  }
  
  @media screen and (max-height: 730px) {
      .text-removal-body {
          max-height: 400px;
      }
      
      .text-removal-container {
          height: 500px;
      }
  }
  
  .text-removal-selected-image {
    width: 300px;
    height: 300px;
    object-fit: contain;
    cursor: pointer;
  }
  
  .text-removal-selected-image:hover {
    border: 2px solid #209c86;
  }
  
  .zoomed-image {
      width: 100%;
      height: auto;
      max-height: 50vh;
      object-fit: contain;
      user-select: none;
      pointer-events: none;
      cursor: crosshair;
      display: block;
      margin: 0 auto;
      -webkit-user-drag: none;
  }
  
  .image-container * {
      user-select: none;
      -webkit-user-drag: none;
  }
  
  .selection-preview {
    position: absolute;
    bottom: 10px;
    right: 10px;
    width: 200px;
    height: 150px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background-color: rgba(0, 0, 0, 0.7);
    border-radius: 5px;
    padding: 10px;
    user-select: none;
    z-index: 102;
    transition: right 0.2s, left 0.2s;
  }
  
  .selection-preview.align-left {
    right: auto;
    left: 10px;
  }
  
  .preview-image {
    max-width: 100%;
    max-height: 100px;
    object-fit: contain;
    margin-bottom: 5px;
  }
  
  .preview-info {
    color: white;
    font-size: 12px;
    text-align: center;
    width: 100%;
  }
  
  .preview-info p {
    margin-bottom: 2px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  
  .size-warning {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #ffcc00;
    margin-left: 5px;
    font-size: 14px;
    background-color: rgba(0, 0, 0, 0.4);
    border-radius: 50%;
    width: 18px;
    height: 18px;
  }
  
  .size-warning i {
    vertical-align: middle;
    line-height: 1;
  }
  
  .btn-primary-outline {
    color: #209c86;
    background: transparent;
    border: 1px solid #209c86;
  }
  .btn-primary-outline:hover:not(:disabled) {
    color: white;
    background: #209c86;
  }
  .btn-primary-outline:disabled {
    color: #6c757d;
    border-color: #6c757d;
  }
  
  .selections-list {
    .selection-item {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 8px;
      border: 2px solid #e0e0e0;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s ease;
      width: 80px;
      
      &.active {
        border-color: #209c86;
        background-color: rgba(32, 156, 134, 0.1);
      }
      
      &:hover {
        border-color: #209c86;
      }
    }
    
    .selection-thumbnail {
      width: 60px;
      height: 60px;
      object-fit: contain;
      border-radius: 4px;
      margin-bottom: 4px;
      background-color: #f8f9fa;
    }
    
    .btn-danger {
      position: absolute;
      top: -5px;
      right: -5px;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      padding: 0;
      font-size: 12px;
      line-height: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #dc3545;
      border-color: #dc3545;
      color: white;
      font-weight: bold;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
      z-index: 10;
    }
    
    .btn-danger:hover {
      background-color: #c82333;
      border-color: #bd2130;
      color: white;
    }
  }

  // Multiselect styles for translation dropdown
  .multiselect-translation {
    min-height: 38px;
  }

  .image-thumbnail {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: 4px;
    flex-shrink: 0;
  }

  .image-thumbnail-placeholder {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
  }

  .image-select-dropdown {
    width: 100%;

    ::v-deep {
      .btn {
        width: 100%;
        cursor: pointer;
        text-align: left;
        display: flex;
        justify-content: space-between;
        align-items: center;
        background-color: #fff;
        border: 1px solid #ced4da;
        color: #495057;

        &:hover {
          background-color: #fff;
          border-color: #b1bbc4;
        }

        &:focus {
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        }
      }

      .dropdown-menu {
        width: 100%;
      }
    }
  }
  </style>

  <style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
  
